// Coordination -> Shop drawings. What each area owes, who owes it, when it
// is due, and where it stands.
//
// Owner, 2026-09-24: "a new tab that links to the signoff of the schedule
// shop drawings for each area ... each shop drawing operates like an
// issue".
//
// THE DUE DATE AND THE SIGN-OFF ARE THE SCHEDULE'S, not this page's. A row
// shows what its schedule activity says, and approving one is what signs
// that activity off. So a date moved on the plan moves the date here with
// nothing to re-save, and nothing on this page can disagree with the plan
// (back/models/shopDrawing carries the reasoning).
//
// OVERDUE IS COMPUTED, never stored: past the date with no sign-off. A
// drawing whose schedule row has gone says SO rather than showing a blank
// date - lost and named beats silent.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { askExportOptions } from "components/Issue/exportOptionsDialog";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Table, Button, Tag, Space, Drawer, Form, Input, Select, Upload,
  message, Empty, Spin, Tooltip, Popconfirm, InputNumber, Modal, DatePicker, Dropdown,
} from "antd";
import {
  PlusOutlined, UploadOutlined, CheckOutlined, CloseOutlined, FileTextOutlined,
  FilePdfOutlined, PrinterOutlined, SendOutlined, EditOutlined, FolderOpenOutlined, RedoOutlined,
  UsergroupAddOutlined, FlagOutlined, MailOutlined, LinkOutlined, DisconnectOutlined, DeleteOutlined,
  DownloadOutlined, DownOutlined, FileExcelOutlined,
} from "@ant-design/icons";
import moment from "moment";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import { url, tokenRef } from "api";
import {
  getShopDrawings, addShopDrawing, setInternalRelease, submitShopDrawing, decideShopDrawing,
  exportShopDrawings,
  getShopDrawingExportOptions,
  fetchShopDrawingsPdf,
  getShopDrawingOptions, setShopDrawingRecipients,
  editShopDrawing,
  bulkEditShopDrawings,
  bulkSetShopDrawingRecipients,
  deleteShopDrawing, bulkDeleteShopDrawings,
  acknowledgeShopDrawing,
  recordDirection,
  downloadRegisterCsv,
  requestReissue, bulkRequestReissue,
  getShopDrawingRecipientDefaults, setShopDrawingRecipientDefaults,
  sendShopDrawing,
} from "api/tracking/shopDrawingsApi";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { usePreference } from "helpers/userPrefs";
import { pairableIssues, pairDrawingToIssue, unpairDrawing } from "api/issueDrawingPair";
import { getDocVersions } from "api/docControl/getDocVersions";
import { activityOptions, emptyActivityMessage } from "./activityOptions";
import { LEAF_SELECT_PROPS } from "helpers/schedule/leafActivityOptions";
import ActivityPathLine from "./ActivityPathLine";
import ResizableTitle from "components/Table/ResizableTitle";
import BulkSheetImportModal from "./BulkSheetImportModal";
import { signOffStar, STAR_COLOR } from "./signOffStar";
import ShopDrawingFilterBar, { useShopDrawingFilters, STATUS_LABEL } from "./ShopDrawingFilterBar";
import { getShopDrawingFacets } from "api/tracking/shopDrawingFilterApi";
import { withSorters } from "./sortColumns";
import ShopDrawingExternalDelivery from "components/Integrations/ShopDrawingExternalDelivery";
import { printPdfBlob } from "./printPdf";
import BulkDownloadButton from "./BulkDownloadButton";
import SendExternalButton from "./SendExternalButton";
import { filterPeopleOption, renderPeopleOption, peopleTagPlaceholder } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, peopleOf, emailValue } from "components/PeoplePicker/peoplePickerOptions";
import { matchOption } from "components/SearchSelect/searchSelect";

// WHO GETS DELIVER (owner, 2026-10-14: "when a responsible tries to upload a
// delivery the button is not showing"). Hidden before on EVERY drawing under a
// signed-off activity - so a drawing requested after its area was signed off
// (RT06 L3 Area B, signed off 5 Oct, drawing requested 14 Oct) could never be
// delivered. A signed-off activity still takes its outstanding sheets (the
// progress rule counts "signed off with sheets not released"), so the drawing
// that has not been delivered yet - requested, or sent back - keeps it, as
// does an open re-issue. Not on a row only SENT to this partner: only a
// responsible delivers, and the server refuses anyone else
// (`youAreResponsible` is only set for partners).
// THREE STATES, AND ONLY THREE (see recipientOptions): on the project, or on
// the roster with no account yet.
const shopDrawingNote = (p) => (p.hasAccount ? "on this project" : "no account yet, will get a link");

const OWED = new Set(["requested", "rejected"]);
// The name column: wide enough for a sheet number and most of a title.
const NAME_WIDTH = 420;
const NAME_MIN = 160;
function canDeliver(r) {
  if (r.youAreResponsible === false) return false;
  // The owner switched this partner's Edit off (Coordination access): no
  // delivering, no acknowledging - the server would refuse both.
  if (r.partnerEditOff) return false;
  return !r.signedOff || !!r.reissueOpen || OWED.has(r.status);
}

// WHY THIS ROW LOOKS ODD (owner, 2026-10-14: "because the activity was signed
// off and in the past, make that warning show under the edit/actions buttons
// in small red"). A drawing still owed whose activity is already signed off.
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch (e) { return ""; } };
function SignedOffNote({ r }) {
  if (!r.signedOff || !OWED.has(r.status)) return null;
  const when = r.signedOffAt ? fmtDay(r.signedOffAt) : "";
  return (
    <div data-testid="sd-signed-off-note" style={{ color: "#cf1322", fontSize: 11, marginTop: 4, lineHeight: 1.3 }}>
      Activity already signed off{when ? ` on ${when}` : ""}{r.signedOffBy ? ` by ${r.signedOffBy}` : ""} - this drawing is still {r.status === "rejected" ? "sent back" : "not delivered"}.
    </div>
  );
}

// DELETE BUTTONS LIVE OUT HERE, not as ternaries in the page: the page is
// big enough that one more branch in its body overflows the hooks lint's path
// count and the build fails with a false "hook called conditionally".
const labelOf = (r) => `${r.sheetNumber ? `${r.sheetNumber} ` : ""}${r.name || ""}`.trim() || "this drawing";
// `row.canDeleteMine`: a partner's own still-requested drawing, when the owner
// has switched their Delete on (Coordination access) - only ever some rows.
function RowDeleteButton({ show, row, onDelete }) {
  if (!show && !row.canDeleteMine) return null;
  return (
    <Popconfirm title={`Delete ${labelOf(row)}? It leaves the register and the schedule's shop drawing progress.`}
                okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => onDelete(row)}>
      <Tooltip title="Delete this shop drawing">
        <Button size="small" danger icon={<DeleteOutlined />} data-testid="sd-row-delete" />
      </Tooltip>
    </Popconfirm>
  );
}
function BulkDeleteButton({ show, count, onClick }) {
  if (!show) return null;
  return (
    <Button size="small" danger icon={<DeleteOutlined />} onClick={onClick} data-testid="sd-bulk-delete">
      Delete {count.toLocaleString()}
    </Button>
  );
}

// Any of these dialogs open? A helper rather than an || chain in the page:
// the page sits at the hooks lint's path limit (see the delete buttons).
const anyOpen = (...xs) => xs.some(Boolean);
const NO_KEYS = { add: false, del: false, raise: false };
// `raise`: a partner raising their own drawing (the owner's Coordination
// access switch, sent as r.partner) - the Add button without Import.
const keysOf = (r) => ({ add: !!r.canAdd, del: !!r.canDelete, raise: !!(r.partner && r.partner.raise) });
// A partner whose Edit switch is off sees no Deliver / Acknowledge.
const markPartnerRows = (r) => (r.partner && !r.partner.deliver
  ? (r.drawings || []).map((d) => ({ ...d, partnerEditOff: true })) : (r.drawings || []));

// THE TEAM'S TOOLBAR BUTTONS, each behind its own key (2026-10-14 CRUD keys):
// Add and Import need shopDrawingAdd, the default recipient list
// shopDrawingEdit. Out here for the same hooks-lint reason as the delete
// buttons above.
function TeamToolbar({ projectId, canAdd, canRaise, canEdit, onAdd, onImport, defaultsBusy, onDefaults }) {
  if (!projectId) return null;
  return (
    <>
      {canRaise && !canAdd ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
          Add shop drawing
        </Button>
      ) : null}
      {canAdd ? (
        <>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
            Add shop drawing
          </Button>
          {/* BULK IMPORT, beside the single add and behind the same key -
              it names recipients, picks activities and decides a drawing's
              identity, so it is the owner side's. */}
          <Button icon={<UploadOutlined />} onClick={onImport}>
            Import sheets
          </Button>
        </>
      ) : null}
      {/* THE PROJECT'S DEFAULT RECIPIENT LIST (owner, 2026-10-05).
          Here rather than in a settings page because it is the same
          list, edited in the same dialog: a second editor for one
          list is a second set of rules about what an address may be.
          It loads the CURRENT default first, so the dialog opens on
          what is actually stored rather than on an empty box that
          would save as "nobody". ITS OWN ICON, not the row's send icon:
          "who receives THIS drawing" and "who receives new ones by default"
          are different things. */}
      {canEdit ? (
        <Button icon={<UsergroupAddOutlined />} loading={defaultsBusy} onClick={onDefaults}>
          Default recipients
        </Button>
      ) : null}
    </>
  );
}

// TICKS FOR EVERYONE WHO SEES THE TABLE: anyone may download the ticked rows
// (owner, 2026-10-21); edit (bulk edit, recipients, re-issue) and delete stay
// behind their own keys in the bar.
function selectionFor({ picked, setPicked }) {
  return {
    selectedRowKeys: picked,
    onChange: (keys) => setPicked(keys.map(String)),
    preserveSelectedRowKeys: false,
  };
}

// BULK BAR - only when something is ticked, and it says WHAT it will act on
// before it is pressed. A count alone is not a scope. Edit buttons need
// shopDrawingEdit, Delete follows canDelete.
function BulkBar({ projectId, rows, picked, canEdit, canDelete, onEdit, onRecipients, onReissue, onDelete, onClear }) {
  if (!picked.length) return null;
  const ticked = () => (rows || []).filter((r) => picked.includes(String(r._id)));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", marginBottom: 8,
                  border: "1px solid #d9d9d9", borderRadius: 6, background: "#fafafa" }}>
      <b>{picked.length.toLocaleString()}</b>
      <span>{picked.length === 1 ? "drawing selected" : "drawings selected"}</span>
      {canEdit ? (
        <>
          <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => onEdit(ticked())}>
            Edit {picked.length.toLocaleString()}
          </Button>
          <Button size="small" onClick={() => onRecipients(ticked())}>
            Recipients
          </Button>
          <Button size="small" icon={<RedoOutlined />} onClick={() => onReissue(ticked())}>
            Request re-issue
          </Button>
        </>
      ) : null}
      <BulkDownloadButton projectId={projectId} ids={(rows || []).filter((r) => picked.includes(String(r._id))).map((r) => String(r._id))} />
      {canEdit ? (
        <SendExternalButton projectId={projectId} ids={(rows || []).filter((r) => picked.includes(String(r._id))).map((r) => String(r._id))} />
      ) : null}
      <BulkDeleteButton show={canDelete} count={picked.length} onClick={onDelete} />
      <Button size="small" onClick={onClear}>Clear</Button>
      {canEdit ? (
        <span style={{ color: "#667085", fontSize: 12, marginLeft: "auto" }}>
          Name is edited one row at a time
        </span>
      ) : null}
    </div>
  );
}

const STATUS_COLOR = {
  requested: "default", submitted: "blue", under_review: "gold", approved: "green", rejected: "red",
};


// THE AREA A SHOP DRAWING CAN NAME, from Levels & Areas (owner, 2026-10-10):
// each level, then "Level · Area" for every area on it - a drawing's area is
// stored as text, and an "Area A" exists on several levels, so the level
// travels with it. Values already on drawings that are not in the list stay
// offered, marked, so editing a row never blanks it.
export function shopAreaOptions(options, keep = []) {
  const levels = (options && options.levels) || [];
  const areas = (options && options.areas) || [];
  const groups = levels.map((l) => ({
    label: l.label || l.value,
    options: [
      { value: l.value, label: `${l.label || l.value} (whole level)` },
      ...areas.filter((a) => a.level === l.value).map((a) => ({ value: `${l.value} · ${a.label}`, label: `${l.value} · ${a.label}` })),
    ],
  }));
  // Areas with no level (an older project) still come through.
  const loose = areas.filter((a) => !a.level || !levels.some((l) => l.value === a.level))
    .map((a) => ({ value: a.label || a.value, label: a.label || a.value }));
  const known = new Set([...groups.flatMap((g) => g.options.map((o) => o.value)), ...loose.map((o) => o.value)]);
  const extra = [...new Set(keep.filter(Boolean))].filter((v) => !known.has(v))
    // Marked only when Levels & Areas IS the source: on a project that has
    // not set it up, every value is "not in" it and the mark would say
    // nothing but noise.
    .map((v) => ({ value: v, label: options && options.levelsSource === "levels-areas" ? `${v} (not in Levels & Areas)` : v }));
  return [...groups.filter((g) => g.options.length), ...loose, ...extra];
}

// THE PICKED ACTIVITY'S FULL PATH, under the picker, wrapping - the selector
// itself can only show one truncated line, and the point of the owner's
// request is to see every parent of the activity being worked in.
function PickedActivity({ form, activities }) {
  const id = Form.useWatch("ganttItemId", form);
  if (!id) return null;
  const a = (activities || []).find((x) => String(x.id) === String(id));
  if (!a) return null;
  return (
    <ActivityPathLine activity={a} prefix="Working in:"
                      level={a.placeLevel || ""}
                      area={a.areaName ? `Area ${a.areaName}` : ""}
                      style={{ marginTop: -16, marginBottom: 16 }} />
  );
}

// Estimated hours x the rate the server priced the drawing at (utils/
// estimateProjection) - the cost report's own pricing.
function projectedCostLine(pj, hours) {
  const h = Number(hours);
  if (!pj || !(h > 0)) return "-";
  if (pj.rateSource === "no-cost") return "no cost";
  if (pj.rate == null) return `no rate set${pj.companyName ? ` for ${pj.companyName}` : ""}`;
  return `$${(Math.round(h * pj.rate * 100) / 100).toLocaleString()} (${h} h x $${pj.rate}/h)`;
}


export default function ShopDrawingsPage() {
  const { projectId: routeProjectId } = useParams();
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  const [rememberedId] = useLastProject(projects);
  const projectId = routeProjectId || rememberedId || "";

  const [rows, setRows] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  // Changing an estimate is its own grant (estimateEdit, owner 2026-10-16).
  const [canEditEstimate, setCanEditEstimate] = useState(false);
  // Add (the team's Add / Import buttons) and delete (super admin, admin,
  // company admin or shopDrawingDelete) - the server's answer, not a guess
  // here. One state, not two: the page is at the hooks lint's path limit.
  const [keys, setKeys] = useState(NO_KEYS);
  const canAdd = keys.add;
  const canDelete = keys.del;
  // A LINK TO ONE DRAWING (owner, 2026-10-05: "link should open the exact
  // shop, and not open the doc control page").
  //
  // The responsible's digest and the delivery mails have pointed at
  // `?drawing=<id>` since they were written, and nothing on this page ever
  // read it - so every one of those links opened the whole register and
  // left the person to find the row. The register's own file button was the
  // same fault from the other side: it pushed `/document-control?doc=<id>`,
  // and Document Control reads `?sheet=`, so even that landed on a list.
  //
  // Read ONCE, from the URL this page was opened with. Not from a watcher:
  // the id is stripped from the address as soon as it is honoured, and a
  // watcher would then re-run with nothing and undo the landing.
  const [focusId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("drawing") || ""; } catch (e) { return ""; }
  });
  // Whether what is on screen is THAT ONE DRAWING, fetched by id because
  // the register's current filters do not include it. Said out loud with a
  // way back, because a table showing one row of four hundred with no
  // explanation reads as a broken filter.
  const [linkedOnly, setLinkedOnly] = useState(false);
  const [focusDone, setFocusDone] = useState(false);
  // WHICH ROWS ARE OPEN. Controlled only because the link has to be able to
  // open one; a person clicking the expander still drives it, through
  // onExpandedRowsChange, so nothing about the normal behaviour changes.
  const [openRows, setOpenRows] = useState([]);
  // The row a link landed on, kept so it stays marked while the person
  // reads it. Cleared by the next ordinary load, like the banner.
  const [markId, setMarkId] = useState("");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deliver, setDeliver] = useState(null);     // { row, file }
  // WHO GETS THE FINISHED THING. Its own dialog rather than a field on the
  // row edit, because it is its own audience with its own consequence:
  // naming somebody here mails a project document to an address a person
  // typed. { row, value } - value is the tag list the Select is holding.
  const [recips, setRecips] = useState(null);
  const [recipsBusy, setRecipsBusy] = useState(false);
  const [defaultsBusy, setDefaultsBusy] = useState(false);
  // Declared here rather than beside its save handler because the row
  // action refers to it, and the columns memo is defined above that.
  // THE DEFAULT, READ BEFORE IT IS SHOWN. Opening an empty dialog and
  // saving it would replace the stored default with nothing - the failure
  // is silent, and the next drawing created would simply reach nobody.
  const openDefaults = useCallback(async () => {
    setDefaultsBusy(true);
    try {
      const r = await getShopDrawingRecipientDefaults(projectId);
      setRecips({
        projectDefault: true,
        value: (r.recipients || []).map((x) => x.email || x.userId).filter(Boolean),
      });
    } catch (e) {
      message.error(e.message || "Could not read the default recipients");
    } finally {
      setDefaultsBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const openRecipients = useCallback((row) => {
    setRecips({ row, value: (row.recipients || []).map((r) => String(r.email || "").toLowerCase()).filter(Boolean) });
  }, []);
  const [form] = Form.useForm();
  const [hoursForm] = Form.useForm();

  useEffect(() => {
    let alive = true;
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json())
      .then((list) => { if (alive) setProjects(Array.isArray(list) ? list : (list?.projects || [])); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);

  // THE TOOLBAR'S FILTERS, ANSWERED BY THE SERVER (owner, 2026-09-29:
  // "an extensive search and filters like the exact one we have for issue
  // manager list", "make sure the backend also takes those filters").
  //
  // Held as ONE object and sent as one query, so a filter cannot be added
  // to the toolbar and forgotten on the way out - and so "what is on
  // screen" is always the answer to the query that is in the URL-shaped
  // state, never a subset the browser happened to narrow.
  // The filter object and its saved filters live in ShopDrawingFilterBar's hook,
  // shared with Document Control.
  const sf = useShopDrawingFilters(projectId);
  const { filters, setFilters, setFilter, anyFilter } = sf;
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  // No drawing had every searched word; the server sent the closest.
  const [searchLoose, setSearchLoose] = useState(false);
  const filterStamp = JSON.stringify(filters);
  // THE WORDS THE PICKERS OFFER. From the rows plus whatever is already
  // chosen - a value that vanishes from its own dropdown the moment it
  // narrows the rows is a control that fights the person using it.
  // THE CONTRACTOR PICKER'S CHOICES: the project's partners, from the server
  // (the rows do not carry the roster); none for a partner.
  const [contractorChoices, setContractorChoices] = useState([]);
  useEffect(() => {
    let alive = true;
    setContractorChoices([]);
    if (!projectId) return undefined;
    getShopDrawingFacets(projectId).then((f) => { if (alive) setContractorChoices((f && f.contractors) || []); }).catch(() => {});
    return () => { alive = false; };
  }, [projectId]);
  const facets = useMemo(() => {
    const areas = new Set(String(filters.area || "").split(",").filter(Boolean));
    const packages = new Set(String(filters.package || "").split(",").filter(Boolean));
    // `rows` IS NULL UNTIL THE FIRST ANSWER - this page keeps "not loaded"
    // and "nothing" apart on purpose, and iterating it unguarded threw
    // "rows is not iterable" on the very first render.
    const levels = new Set(String(filters.level || "").split(",").filter(Boolean));
    for (const r of rows || []) { if (r.area) areas.add(r.area); if (r.package) packages.add(r.package); if (r.level) levels.add(r.level); }
    const sort = (set) => [...set].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    return { areas: sort(areas), packages: sort(packages), levels: sort(levels), contractors: contractorChoices };
  }, [rows, filterStamp, contractorChoices]); // eslint-disable-line react-hooks/exhaustive-deps

  // THE ADVANCED FILTER (owner, 2026-10-15: "the advanced filter for shop
  // drawings too, to detect name, activity and same capability and able to
  // save it so it can be pulled from cost contractor"). The activity picker
  // offers the whole schedule with its path (an area's Shop drawings row
  // brings the system rows below it - the server widens it); area sign-off
  // is the issue list's, judged on the drawings (utils/issueAreaSignOff,
  // default stage Shop drawings). Saved filters are the person's own,
  // stored with the table's exact query so the cost page can apply them.
  // THE NAME COLUMN'S WIDTH (owner, 2026-10-19: "make the shop drawing name
  // column larger ... hide the rest with ... and have a tooltip that shows the
  // full name"). Drag the header's right edge; remembered per person.
  const [nameWidth, setNameWidth] = usePreference("shopDrawings.nameColumnWidth", NAME_WIDTH);
  const nameW = Math.max(NAME_MIN, Number(nameWidth) || NAME_WIDTH);
  // THE SORT, remembered per person (owner, 2026-10-22): { key, order }, or
  // null for the register's own order.
  const [sortPref, setSortPref] = usePreference("shopDrawings.sort", null);
  const onTableChange = useCallback((_p, _f, sorter, extra) => {
    if (!extra || extra.action !== "sort") return;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortPref(s && s.order && s.field ? { key: String(s.field), order: s.order } : null);
  }, [setSortPref]);
  // What the exports send: the toolbar's filter, exactly as the table reads it.
  const exportFilterParams = anyFilter ? { ...filters, tzOffset: new Date().getTimezoneOffset() } : null;

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    setBusy(true);
    try {
      const q = { filters: 1, tzOffset: new Date().getTimezoneOffset() };
      for (const [k, v] of Object.entries(filters)) q[k] = Array.isArray(v) ? v.join(",") : v;
      const r = await getShopDrawings(projectId, q);
      // ANY ORDINARY LOAD LEAVES THE ONE-DRAWING VIEW. Without this the
      // banner would keep claiming "one drawing from a link" over a table
      // that had since been refiltered - a sentence about the wrong rows.
      setLinkedOnly(false);
      setMarkId("");
      setRows(markPartnerRows(r));
      setCanEdit(!!r.canEdit);
      setCanEditEstimate(!!r.canEditEstimate);
      setKeys(keysOf(r));
      setTotal(r.total === undefined ? (r.drawings || []).length : r.total);
      setCapped(!!r.capped);
      setSearchLoose(!!r.searchLoose);
    } catch (e) {
      message.error(e.message || "Could not read the shop drawings");
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [projectId, filterStamp]); // eslint-disable-line react-hooks/exhaustive-deps
  // THE SEARCH BOX IS DEBOUNCED, the rest are not: a name arrives keystroke
  // by keystroke and a select arrives once.
  useEffect(() => {
    const t = setTimeout(load, filters.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps


  // HOURS ARE REQUIRED ON DELIVERY, the same rule an issue resolution
  // keeps - asked for here, with the file already chosen, rather than
  // refused by the server afterwards.
  // ONE DELIVERY PER CLICK (owner, 2026-10-12): the upload takes a moment,
  // and a second click on Deliver while it ran sent the delivery - and its
  // "delivered" email to every owner-side person - twice. The button waits.
  const [delivering, setDelivering] = useState(false);
  const deliveringRef = useRef(false);
  const sendDelivery = async ({ hoursSpent }) => {
    if (deliveringRef.current) return;
    deliveringRef.current = true;
    setDelivering(true);
    try {
      await submitShopDrawing(projectId, deliver.row._id, { file: deliver.file, hoursSpent });
      message.success("Delivered - filed in Document Control and waiting for approval");
      setDeliver(null);
      hoursForm.resetFields();
      load();
    } catch (e) {
      message.error(e.message || "Could not deliver the drawing");
    } finally {
      deliveringRef.current = false;
      setDelivering(false);
    }
  };

  // The register, downloaded. The button says what it is doing, because
  // a PDF of a long register is not instant and a silent button reads as
  // a broken one.
  const [exporting, setExporting] = useState(false);
  // PDF, Excel or CSV (owner, 2026-10-19): the rows on screen, as filtered.
  const onExport = useCallback(async (ext) => {
    // WHAT GOES IN IT, ASKED FIRST (owner, 2026-10-22: "do the same for shop
    // drawings export"). Same dialog and same server gates as the issue
    // manager; with no grant there is no popup and nothing changes.
    let include;
    try {
      const allowed = await getShopDrawingExportOptions(projectId);
      const answer = await askExportOptions({ allowed, formatName: { pdf: "PDF", xlsx: "Excel file", csv: "CSV" }[ext] || "export" });
      if (answer === null) return;
      if (allowed && (allowed.hours || allowed.prices)) include = answer;
    } catch (e) {
      include = undefined;
    }
    setExporting(true);
    try {
      const { count } = await exportShopDrawings(projectId, ext, {
        filters: exportFilterParams,
        ...(include ? { includeHours: include.hours, includeEstimates: include.estimates } : {}),
      });
      const what = { pdf: "PDF", xlsx: "Excel file", csv: "CSV" }[ext] || "File";
      message.success(count === null || count === undefined
        ? `${what} downloaded`
        : `${what} downloaded - ${count} ${count === 1 ? "drawing" : "drawings"}`);
    } catch (e) {
      message.error(e.message || "Could not build the export");
    } finally {
      setExporting(false);
    }
  }, [projectId, filterStamp]); // eslint-disable-line react-hooks/exhaustive-deps

  // PRINT IS THE EXPORT, PRINTED (owner, 2026-10-13): the same PDF, opened
  // in the browser's print dialog - never the screen table, which has no
  // print layout.
  const [printing, setPrinting] = useState(false);
  const onPrint = useCallback(async () => {
    setPrinting(true);
    try {
      const { blob } = await fetchShopDrawingsPdf(projectId, { filters: exportFilterParams });
      const how = await printPdfBlob(blob);
      if (how === "tab") message.info("The register opened in a new tab - print it from there");
      else if (how === "blocked") message.warning("Your browser blocked the print window - use Export, PDF and print the file");
    } catch (e) {
      message.error(e.message || "Could not build the register");
    } finally {
      setPrinting(false);
    }
  }, [projectId, filterStamp]); // eslint-disable-line react-hooks/exhaustive-deps

  // VALIDATE AND "SIGN OFF THE AREA" ARE TWO ACTS NOW.
  //
  // Owner, 2026-10-08, after validating one sheet and finding the area signed
  // off: "it should be explecitly asking on the sheet side, if this is last
  // sheet needed for sign off as a button to cclose/sign off an area, next to
  // validate on the shop drawing side".
  //
  // Validating says the drawing is good. Signing the area off says no further
  // sheet is coming, which is a judgement only the reviewer holds - nothing in
  // the register says it is complete. So the second is its own button and the
  // message after a plain validate SAYS the area was left alone, because the
  // old message claimed a sign-off and that is how three areas went out.
  const decide = useCallback(async (row, approve, note, signOffArea = false) => {
    try {
      const r = await decideShopDrawing(projectId, row._id, { approve, note, signOffArea });
      if (!approve) {
        message.success("Returned to the responsible - the due date has not changed");
      } else if (r.areaSignedOff) {
        message.success(`Validated - "${r.activity?.name || r.drawing.scheduleName || "the area"}" is signed off`);
      } else {
        // WHAT IS STILL OUTSTANDING, in the same words the schedule uses.
        const d = r.areaDrawings || {};
        const left = d.outstanding
          ? `${d.outstanding} of ${d.total} not delivered yet`
          : d.total > 1 ? `${d.delivered} of ${d.total} delivered` : "";
        message.success(`Validated. "${r.activity?.name || "The area"}" is not signed off${left ? ` - ${left}` : ""}`);
      }
      load();
    } catch (e) {
      // The schedule's own gate refused - an undelivered drawing on the
      // activity, an open issue, or a child not signed off. Its words, not
      // ours: the server names the sheets.
      message.error(e.message || "Could not decide the drawing");
    }
  }, [projectId, load]);

  // The explicit one. It asks, because it is the assertion that cannot be
  // checked - and it names the activity so nobody signs off the wrong area.
  const askAndSignOffArea = useCallback((row) => {
    Modal.confirm({
      title: `Is this the last sheet needed for "${row.scheduleName || "this area"}"?`,
      width: 520,
      content: (
        <div style={{ fontSize: 13 }}>
          <p style={{ marginBottom: 8 }}>
            This validates <b>{row.name}</b> and signs off the area. Only do it if
            no further sheet is expected - nothing in the register can tell us that.
          </p>
          <p style={{ marginBottom: 0, color: "#667085" }}>
            If any other shop drawing on the activity has not been delivered, this is
            refused and the sheets are named.
          </p>
        </div>
      ),
      okText: "Validate and sign off the area",
      onOk: () => decide(row, true, "", true),
    });
  }, [decide]);

  // SEND / RESEND the approved deliverable (owner, 2026-10-10: "once they
  // are delivered and approved, the button should say resend"). Not-sent is
  // said, never implied: the server answers with the count and the error.
  const [sending, setSending] = useState("");
  const sendNow = useCallback(async (row) => {
    setSending(String(row._id));
    try {
      const r = await sendShopDrawing(projectId, row._id);
      const verb = r.resent ? "Resent" : "Sent";
      if (r.lastError) message.warning(`${verb} to ${r.mailed}, but not everyone: ${r.lastError}`);
      else if (!r.mailed) message.warning("Nobody to send it to - add recipients first");
      else message.success(`${verb} to ${r.mailed} recipient(s)`);
      load();
    } catch (e) {
      message.error(e.message || "Could not send the drawing");
    } finally {
      setSending("");
    }
  }, [projectId, load]);

  // LINK AN EXISTING DRAWING TO AN ISSUE, OR UNLINK IT (owner, 2026-10-08:
  // "once a selection made from either sides, the other side should reflect
  // the link"). The issues offered come from the server, as in the add form.
  const [linking, setLinking] = useState(null);   // { row, options, chosen, busy }
  const openLink = useCallback(async (row) => {
    setLinking({ row, options: null, chosen: undefined, busy: false });
    try {
      const d = await pairableIssues(projectId, row._id);
      setLinking((cur) => (cur && cur.row._id === row._id ? { ...cur, options: (d && d.issues) || [] } : cur));
    } catch (e) {
      message.error(e.message || "Could not list the issues");
      setLinking(null);
    }
  }, [projectId]);
  const saveLink = useCallback(async () => {
    if (!linking || !linking.chosen) return;
    setLinking((cur) => ({ ...cur, busy: true }));
    try {
      await pairDrawingToIssue(projectId, linking.row._id, linking.chosen);
      message.success("Linked - the two now count one set of hours");
      setLinking(null);
      load();
    } catch (e) {
      message.error(e.message || "Could not link them");
      setLinking((cur) => (cur ? { ...cur, busy: false } : cur));
    }
  }, [linking, projectId, load]);
  const unlinkRow = useCallback(async (row) => {
    try {
      await unpairDrawing(projectId, row._id);
      message.success("Unlinked. Hours already entered were left where they are.");
      load();
    } catch (e) {
      message.error(e.message || "Could not unlink them");
    }
  }, [projectId, load]);

  const askAndReturn = useCallback((row) => {
    let note = "";
    Modal.confirm({
      title: `Return "${row.name}"?`,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>The due date does not change - it goes back to the responsible as it stands.</p>
          <Input.TextArea rows={3} placeholder="What needs fixing" onChange={(e) => { note = e.target.value; }} />
        </div>
      ),
      okText: "Return it",
      onOk: () => decide(row, false, note),
    });
  }, [decide]);

  // EDIT - ONE ROW OR MANY, THROUGH ONE FORM (owner, 2026-09-30: "I need edit
  // option for the shop drawings, per row and bulk").
  //
  // ONE editor for both, deliberately. Two forms over the same allowlist drift -
  // a field gets added to the row one and not the bulk one, and nobody notices
  // until somebody needs it on sixty rows. `edit.ids` carries which rows: one
  // for a row edit, many for a bulk edit, and the only difference in the UI is
  // the title and which fields are left blank.
  //
  // BLANK MEANS LEAVE ALONE, in the bulk case. That is why the fields start
  // empty rather than pre-filled from the first row: pre-filling would write the
  // first row's area onto all of them the moment somebody pressed Save.
  const [edit, setEdit] = useState(null);       // { ids: [], title, initial }
  // BULK SHEET IMPORT (owner, 2026-10-01: "i still don't see the bulk import
  // of sheets on the shop drawings"). Its own dialog, because the mapping grid
  // is a screen rather than a field - and it STAYS OPEN while he works down it.
  const [importing, setImporting] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editForm] = Form.useForm();
  // WATCHED, not read once: the orphan sentinel must disappear the moment
  // a real activity is picked, and reappear if the field is cleared back
  // to the dead value. Reading edit.initial would freeze it at open.
  const editActivityValue = Form.useWatch("ganttItemId", editForm);

  // THE ISSUES THIS DRAWING MAY BE PAIRED WITH - one piece of work, one set of
  // hours (owner, 2026-10-08: "in the shop drawing side when we request a shop
  // drawing we should hava an option to select an issue"). The server decides
  // which are offerable; a browser-side filter would be a second opinion the
  // server then refuses after the person has already chosen.
  const [pairIssues, setPairIssues] = useState([]);
  useEffect(() => {
    if (!projectId) { setPairIssues([]); return undefined; }
    let cancelled = false;
    pairableIssues(projectId)
      .then((d) => { if (!cancelled) setPairIssues((d && d.issues) || []); })
      .catch(() => { if (!cancelled) setPairIssues([]); });
    return () => { cancelled = true; };
  }, [projectId]);
  // ARRIVING FROM AN EXTRA-SCOPE ISSUE (owner, 2026-10-21: an issue raised in
  // scope the owner/GC signed off "should trigger a shop drawing"). The issue
  // create page sends the person here with ?pairIssue=<id>, so the Add drawer
  // opens with that issue already in the pair field - without this the link
  // lands on a register and they have to find their own issue again, which is
  // how a prompt becomes an annoyance instead of a shortcut.
  //
  // IT WAITS FOR THE OFFERABLE LIST. Setting the field before pairIssues has
  // loaded leaves a Select showing a raw id; and `pairHandled` means a
  // re-render never re-opens the drawer over whatever the person went on to do.
  //
  // window.location, NOT useLocation: this is read once on mount and nothing
  // navigates to a different pairIssue while the page is open, so the router's
  // reactive copy buys nothing - and reaching for it would mean changing the
  // react-router mock in seven existing test files for a value none of them
  // care about.
  const pairFromUrl = new URLSearchParams(window.location.search || "").get("pairIssue") || "";
  const [pairHandled, setPairHandled] = useState(false);
  useEffect(() => {
    if (!pairFromUrl || pairHandled || !pairIssues.length) return;
    if (!pairIssues.some((i) => String(i._id) === pairFromUrl)) {
      // Not offerable - already paired, or resolved. Say so rather than
      // opening a drawer with a field that cannot be set.
      message.info("That issue can no longer be paired with a new shop drawing");
      setPairHandled(true);
      return;
    }
    setPairHandled(true);
    setAdding(true);
    form.setFieldsValue({ pairedIssueId: pairFromUrl });
  }, [pairFromUrl, pairHandled, pairIssues, form]);

  const [picked, setPicked] = useState([]);     // bulk selection

  // REVISION HISTORY, WITH A DOWNLOAD PER REVISION (owner, 2026-09-30, gap 4:
  // "show prior revs with a Superseded tag and delivered dates", then "as long
  // as you keep access for direct download of old revisions").
  //
  // FETCHED WHEN A ROW IS OPENED, not with the table. The download urls carry a
  // ten-minute signed token from /doc-control/:id/versions, so urls fetched with
  // the page and clicked later would be expired - and the server's own answer to
  // that is "open the version list again", which is exactly what this does.
  // THE RE-ISSUE ASK (owner, 2026-10-05: "a button that requests re issue of
  // any shop drawing or a bulk request for multiple shop drawings").
  //
  // { rows, reason, dueAt } - `rows` is one drawing or the whole tick, so
  // the single and bulk asks are one dialog and cannot say different things
  // about the same action.
  const [reissue, setReissue] = useState(null);
  const [reissueBusy, setReissueBusy] = useState(false);
  // WHICH ROW'S FILE IS BEING FETCHED, so its button spins rather than the
  // whole page: opening a drawing is one row's business.
  const [opening, setOpening] = useState("");
  const [revs, setRevs] = useState({});          // docControlId -> { loading, list, err }
  const openRevs = useCallback(async (docControlId) => {
    const key = String(docControlId || "");
    if (!key) return;
    setRevs((r) => ({ ...r, [key]: { loading: true, list: (r[key] && r[key].list) || [], err: "" } }));
    try {
      const data = await getDocVersions(key);
      const list = (data && data.versions) || [];
      setRevs((r) => ({ ...r, [key]: { loading: false, list, err: "" } }));
    } catch (e) {
      // SAYS SO rather than showing an empty history: an empty list and a failed
      // read look identical, and one of them means "there are no revisions".
      setRevs((r) => ({ ...r, [key]: { loading: false, list: [], err: e.message || "Could not read the revisions" } }));
    }
  }, []);

  // LAND ON THE DRAWING THE LINK NAMED.
  //
  // DECLARED AFTER openRevs because it CALLS it: a hook may move down the
  // file freely (the order of hooks is what React cares about, and this is
  // still unconditional and still in the same order every render), but a
  // reference above its definition is a temporal dead zone - the fault that
  // has produced two white pages on this project.
  //
  // Three cases, and the third is the one that makes this worth writing:
  //   it is in the rows already      -> open it, scroll to it, mark it;
  //   it is not (a filter, the cap)  -> fetch THAT drawing by id and show
  //                                     it, saying so, with a way back;
  //   it is gone                     -> say that, rather than showing the
  //                                     register as though the link had
  //                                     worked. A deleted drawing is an
  //                                     answer; a silent list is not.
  //
  // Runs once per link (focusDone), and never fights the person: the
  // moment it has landed, the id is out of the address bar.
  useEffect(() => {
    if (!focusId || focusDone || !projectId || rows === null) return;
    const here = (rows || []).find((r) => String(r._id) === String(focusId));
    const land = (row) => {
      setFocusDone(true);
      setMarkId(String(row._id));
      setOpenRows([String(row._id)]);
      // THE PANEL'S CONTENTS HAVE TO BE ASKED FOR. `onExpand` fires for a
      // PERSON opening a row, not for a row opened by setting
      // expandedRowKeys - so without this the link landed on a row whose
      // revision panel said "Reading the revisions…" for ever. Found by a
      // test asserting the fetch rather than the open state.
      if (row.docControlId) openRevs(row.docControlId);
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("drawing");
        window.history.replaceState({}, "", u.pathname + (u.search || "") + (u.hash || ""));
      } catch (e) { /* the landing matters, the tidy address does not */ }
      // After the row renders expanded, not before.
      setTimeout(() => {
        try {
          const el = document.querySelector(`[data-row-key="${String(row._id)}"]`);
          if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
        } catch (e) { /* a row that cannot be scrolled to is still open */ }
      }, 60);
    };
    if (here) { land(here); return; }
    setFocusDone(true);
    (async () => {
      try {
        const r = await getShopDrawings(projectId, { filters: 1, ids: String(focusId) });
        const one = (r.drawings || [])[0];
        if (!one) {
          message.warning("That shop drawing is no longer on this project - it may have been removed. Showing the register instead.");
          return;
        }
        setRows([one]);
        setTotal(1);
        setCapped(false);
        setLinkedOnly(true);
        land(one);
      } catch (e) {
        message.error(e.message || "Could not open that shop drawing");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, focusDone, projectId, rows]);

  // SEND THE RE-ISSUE ASK, one drawing or many, through the one handler.
  //
  // THE ANSWER IS READ, NOT ASSUMED. The bulk route reports what it did and
  // what it SKIPPED, by name - a drawing with nothing delivered has no
  // revision to re-issue, and one that was already sent back is already
  // sent back. Reporting "12 requested" over four skipped rows is the
  // failure this register has twice paid for, so the skipped ones are said
  // out loud, with their reason, and the table is reloaded either way.
  const sendReissue = useCallback(async () => {
    const r = reissue;
    if (!r) return;
    // THE REASON IS OPTIONAL (owner, 2026-10-05). Asked for in the dialog,
    // never required: the common ask is one he has already made on a call,
    // and a field he has to fill to send it is friction on every one of
    // those. With none, the mail simply carries no reason paragraph.
    const reason = String(r.reason || "").trim();
    const ids = (r.rows || []).map((x) => String(x._id));
    const dueAt = r.dueAt ? r.dueAt.toISOString() : null;
    setReissueBusy(true);
    try {
      if (ids.length === 1) {
        await requestReissue(projectId, ids[0], { reason, dueAt });
        message.success("Asked for a re-issue - the responsible has been told");
      } else {
        const out = await bulkRequestReissue(projectId, ids, { reason, dueAt });
        const skipped = out.skipped || [];
        message.success(`${Number(out.opened || 0).toLocaleString()} of ${ids.length.toLocaleString()} asked for`);
        if (skipped.length) {
          Modal.info({
            title: `${skipped.length} ${skipped.length === 1 ? "drawing was" : "drawings were"} not asked for`,
            content: (
              <div style={{ maxHeight: 260, overflow: "auto" }}>
                {skipped.map((x) => (
                  <div key={x.id} style={{ marginBottom: 4 }}>
                    <b>{x.name}</b><br />
                    <span style={{ color: "#667085" }}>{x.why}</span>
                  </div>
                ))}
              </div>
            ),
            getContainer: () => document.body,
          });
        }
        if (out.missing) message.warning(`${out.missing} of them are no longer on the project`);
      }
      setReissue(null);
      setPicked([]);
      load();
    } catch (e) {
      message.error(e.message || "Could not ask for a re-issue");
    } finally {
      setReissueBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reissue, projectId]);

  // OPEN THE DRAWING ITSELF - the latest revision filed.
  //
  // The versions route is the one that already authorises this and signs
  // its own urls (api/docControl/getDocVersions); nothing here builds a
  // storage path. The newest revision is the one by HIGHEST rev, not the
  // last in the array - the server's order is its own business and a
  // reader that depends on it is a bug waiting for a sort to change.
  //
  // EVERY OUTCOME SAYS SOMETHING. A document with no file on this server,
  // a read that failed, a popup the browser blocked: each one is a
  // sentence, because a button that silently does nothing is the thing
  // this change exists to remove.
  const openDrawingFile = useCallback(async (row) => {
    const key = String((row && row.docControlId) || "");
    if (!key) return;
    setOpening(String(row._id));
    try {
      const data = await getDocVersions(key);
      const list = (data && data.versions) || [];
      if (!list.length) {
        message.info("Nothing is filed against this drawing yet - no revision to open.");
        return;
      }
      const newest = list.reduce((hi, v) => (Number(v.rev) > Number(hi.rev) ? v : hi), list[0]);
      if (!newest || !newest.url) {
        message.warning(`Revision ${newest ? newest.rev : "?"} has no file on this server, so there is nothing to open. Its record in Document Control still shows what was filed.`);
        return;
      }
      const w = window.open(`${url}${newest.url}`, "_blank", "noopener");
      if (!w) message.warning("Your browser blocked the new tab - allow popups for this site, or use the revision list under the row.");
    } catch (e) {
      message.error(e.message || "Could not open that drawing");
    } finally {
      setOpening("");
    }
  }, []);

  // GAP 5. Offered only once the thing has actually been delivered - there is
  // nothing to acknowledge receiving before it was sent, and a button that
  // records receipt of an unsent drawing would make the timestamp meaningless.
  const acknowledge = useCallback(async (r) => {
    try {
      const res = await acknowledgeShopDrawing(projectId, r._id);
      message.success(res.already ? "Already acknowledged - the first date is kept" : "Recorded as received by the field");
      load();
    } catch (e) {
      message.error(e.message || "Could not record it");
    }
  }, [projectId, load]);

  const [registering, setRegistering] = useState(false);
  const onRegister = useCallback(async (format = "csv") => {
    setRegistering(format);
    try {
      const { count, note } = await downloadRegisterCsv(projectId, { ...(exportFilterParams ? { ...exportFilterParams, filters: 1 } : {}), format });
      // NOTHING DOWNLOADED IS STILL AN ANSWER, and it is the one worth saying
      // out loud: on this project today, no drawing is both delivered and
      // signed off, so the file is a header and no rows.
      if (!count) message.warning("Nothing is both delivered and signed off yet - the file has the columns and no rows");
      // (the same for every format: PDF, Excel and CSV are one register)
      else message.success(`${count} ${count === 1 ? "drawing" : "drawings"} exported as ${({ csv: "CSV", xlsx: "Excel", pdf: "PDF" })[format] || "CSV"}`);
      // The missing drawing NUMBER, said once per export rather than hidden in a
      // blank column.
      if (note) message.info(note, 8);
    } catch (e) {
      message.error(e.message || "Could not build the register");
    } finally {
      setRegistering(false);
    }
  }, [filterStamp, projectId, filters.status, filters.area]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = useCallback((rows_) => {
    const list = Array.isArray(rows_) ? rows_ : [rows_];
    const many = list.length > 1;
    // A row edit starts FROM the row, so the person corrects what is there. A
    // bulk edit starts blank, so nothing is written that was not typed.
    //
    // THE VALUES TRAVEL ON THE STATE, AS initialValues - NOT PUSHED IN WITH
    // setFieldsValue. Owner, 2026-10-01: "edit shopdrawings, sometimes,
    // doesn't show the fields populated, although it is edit".
    //
    // "Sometimes" was once per page load. Driven, three opens in a row:
    //   open 1 on a row  -> ["Busway L1", "Power"]   populated
    //   close, open row 2 -> []                      EMPTY
    //   close, open row 1 -> []                      EMPTY
    // The Modal is destroyOnClose and the Form is preserve={false}, so closing
    // UNMOUNTS the form and discards its store. openEdit then called
    // setFieldsValue synchronously, BEFORE setEdit had re-rendered and
    // remounted it - so the write raced the unmount's clear, and from the
    // second open onwards the clear won. The first open worked only because
    // there was nothing to clear yet.
    //
    // Fixed by not writing imperatively at all: a fresh `key` remounts the
    // form on every open and initialValues is read AT mount, so there is no
    // window in which to lose them.
    setEdit({
      ids: list.map((r) => String(r._id)),
      title: many ? `Edit ${list.length} shop drawings` : `Edit "${list[0].name}"`,
      many,
      initial: many
        ? {}
        : {
          name: list[0].name,
          description: list[0].description,
          area: list[0].area,
          package: list[0].package,
          system: list[0].system,
          sheetNumber: list[0].sheetNumber,
          estimatedHours: list[0].estimatedHours == null ? undefined : list[0].estimatedHours,
          ganttItemId: list[0].ganttItemId ? String(list[0].ganttItemId) : undefined,
        },
      projection: many ? null : (list[0].projection || null),
    });
  }, []);

  const saveEdit = useCallback(async (vals) => {
    if (!edit) return;
    // Only what was actually typed. undefined is dropped; an empty string is
    // KEPT, because clearing a field is a real edit and the server treats the
    // two differently.
    const set = {};
    for (const k of ["name", "description", "area", "package", "system", "ganttItemId"]) {
      if (vals[k] !== undefined) set[k] = vals[k];
    }
    // An estimate is set or corrected, never cleared (the server refuses it).
    if (vals.estimatedHours != null && canEditEstimate
      && !(edit.initial && edit.initial.estimatedHours === vals.estimatedHours)) set.estimatedHours = vals.estimatedHours;
    if (!Object.keys(set).length) { message.info("Nothing to change"); return; }
    // A bulk edit CANNOT set the name - sixty drawings with one name is not a
    // register. The field is hidden for many, and this is the guard behind it.
    if (edit.many) delete set.name;
    setEditBusy(true);
    try {
      if (edit.ids.length === 1) {
        const r = await editShopDrawing(projectId, edit.ids[0], set);
        message.success(`Saved: ${(r.changed || []).join(", ") || "no change"}`);
        if ((r.rejected || []).length) message.warning(`Not editable here: ${r.rejected.join(", ")}`);
      } else {
        const r = await bulkEditShopDrawings(projectId, edit.ids, set);
        // THE NUMBERS THE SERVER ACTUALLY RETURNED, not "done". found can be
        // lower than asked when a row was deleted under them, and
        // alreadyHadValues says how many were already right - which is why the
        // modified count on its own would read as a partial failure.
        const bits = [`${r.modified} of ${r.found} changed`];
        if (r.alreadyHadValues) bits.push(`${r.alreadyHadValues} already had those values`);
        if (r.missing) bits.push(`${r.missing} no longer exist`);
        message.success(bits.join(" · "));
        if ((r.rejected || []).length) message.warning(`Not editable here: ${r.rejected.join(", ")}`);
      }
      setEdit(null);
      setPicked([]);
      load();
    } catch (e) {
      message.error(e.message || "Could not save");
    } finally {
      setEditBusy(false);
    }
  }, [edit, projectId, load, canEditEstimate]);

  // DELETE, one row or the ticked ones (owner, 2026-10-14). The confirm names
  // what goes; the message reports what the server says it removed.
  const deleteOne = useCallback(async (r) => {
    try {
      await deleteShopDrawing(projectId, r._id);
      message.success(`Deleted ${labelOf(r)}`);
      setPicked((p) => p.filter((id) => id !== String(r._id)));
      load();
    } catch (e) {
      message.error(e.message || "Could not delete the shop drawing");
    }
  }, [projectId, load]);
  const deletePicked = useCallback(() => {
    const sel = (rows || []).filter((r) => picked.includes(String(r._id)));
    if (!sel.length) return;
    Modal.confirm({
      title: `Delete ${sel.length.toLocaleString()} shop drawing${sel.length === 1 ? "" : "s"}?`,
      content: (
        <div>
          <div style={{ maxHeight: 220, overflow: "auto", margin: "6px 0" }}>
            {sel.slice(0, 50).map((r) => <div key={r._id}>{labelOf(r)}</div>)}
            {sel.length > 50 ? <div>and {(sel.length - 50).toLocaleString()} more</div> : null}
          </div>
          <div style={{ color: "#667085", fontSize: 12 }}>
            They leave the register and the schedule's shop drawing progress. A link to an issue is removed; hours already entered stay.
          </div>
        </div>
      ),
      okText: "Delete", okType: "danger", cancelText: "Cancel",
      onOk: async () => {
        try {
          const r = await bulkDeleteShopDrawings(projectId, sel.map((x) => String(x._id)));
          const bits = [`Deleted ${r.deleted} of ${r.asked}`];
          if (r.missing) bits.push(`${r.missing} were already gone`);
          message.success(bits.join(" · "));
          setPicked([]);
          load();
        } catch (e) {
          message.error(e.message || "Could not delete the shop drawings");
        }
      },
    });
  }, [rows, picked, projectId, load]);

  const columns = useMemo(() => withSorters([
    {
      title: "Shop drawing",
      dataIndex: "name",
      width: nameW,
      onHeaderCell: () => ({ width: nameW, onResize: setNameWidth }),
      render: (v, r) => (
        <div style={{ width: nameW - 16, maxWidth: nameW - 16 }}>
          {/* ONE LINE, CUT WITH "...", THE WHOLE NAME ON HOVER. */}
          <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.3}>
            <div data-testid="sd-name" style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v}</div>
          </Tooltip>
          {/* THE ACTIVITY IT HANGS OFF, AS THE DIGEST PRINTS IT (owner,
              2026-10-13) - so the row says which parent it is under. */}
          <ActivityPathLine activity={{ activityFullPath: r.activityFullPath, name: r.scheduleName }}
                            missing={!!r.scheduleMissing}
                            area={r.areaName && !(r.activityFullPath || "").includes(r.areaName) ? `Area ${r.areaName}` : ""} />
          {r.description ? <div style={{ color: "#667085", fontSize: 12 }}>{r.description}</div> : null}
          {/* THE PAIRED ISSUE - one piece of work, one set of hours. */}
          {r.pairedIssue ? (
            <div style={{ fontSize: 12, marginTop: 2 }}>
              <LinkOutlined style={{ color: "#667085", marginRight: 4 }} />
              {r.pairedIssue.shareToken ? (
                <a onClick={(e) => { e.preventDefault(); history.push(`/issue/${r.pairedIssue.shareToken}/resolve`); }}
                   href={`/issue/${r.pairedIssue.shareToken}/resolve`}>
                  Issue {r.pairedIssue.label}
                </a>
              ) : <span>Issue {r.pairedIssue.label}</span>}
              {r.pairedIssue.status ? <span style={{ color: "#98a2b3" }}> · {r.pairedIssue.status}</span> : null}
            </div>
          ) : null}
        </div>
      ),
    },
    { title: "Area", dataIndex: "area", width: 100 },
    { title: "Package", dataIndex: "package", width: 110 },
    // THE REQUEST (owner, 2026-09-29). Both come from the row's own birth -
    // a drawing is created 'requested' - so there is no second date to drift.
    {
      title: "Requested",
      dataIndex: "requestedAt",
      width: 150,
      render: (v, r) => (v ? (
        <div>
          <div>{moment(v).format("D MMM YYYY")}</div>
          {r.requestedBy ? <div style={{ color: "#667085", fontSize: 12 }}>by {r.requestedBy}</div> : null}
        </div>
      ) : <span style={{ color: "#98a2b3" }}>-</span>),
    },
    {
      title: "Produced by",
      dataIndex: "producedBy",
      width: 140,
      render: (v, r) => (v
        ? (
          <div>
            <div>{v}</div>
            {r.submittedAt ? <div style={{ color: "#667085", fontSize: 12 }}>{moment(r.submittedAt).format("D MMM")}</div> : null}
          </div>
        )
        : <span style={{ color: "#98a2b3" }}>not submitted</span>),
    },
    // DELIVERED - when the approved deliverable actually went out, and to
    // whom it went, from the announcement's own record of each successful
    // send. `deliveredTo` is absent for a partner by design (one firm must
    // not read another's guest list), so the column says nothing rather
    // than saying "none" - absent is not empty.
    {
      title: "Delivered",
      dataIndex: "deliveredAt",
      width: 190,
      render: (v, r) => {
        if (!v) {
          if (r.deliveryPending) return <Tag color="blue">sending</Tag>;
          // HELD BEFORE FAILED, because a hold is not a failure. Three rows
          // showed a red "send failed" in October where nothing had failed -
          // they were parked at the owner's request - and he read the column
          // as lying to him. An amber "held by request" says what happened.
          if (r.deliveryHeld) return <Tag color="orange">held by request</Tag>;
          if (r.deliveryFailed) return <Tag color="red">send failed</Tag>;
          return <span style={{ color: "#98a2b3" }}>not delivered</span>;
        }
        const to = r.deliveredTo;
        return (
          <div>
            <div>{moment(v).format("D MMM YYYY")}</div>
            {/* FULL NAME (CONTRACTOR), not an id and not an address
                (owner, 2026-10-05). The label is built on the SERVER
                (back/utils/recipientLabel) so the tooltip, the mail's own
                To header and anything else that prints a recipient read
                the same words - a copy of the rule here would drift from
                the mail's within a week.

                A bare typed address keeps its address, because that is
                who the person is: this list exists so that somebody who
                is on no roster and has no account can be sent the
                finished drawing. */}
            {Array.isArray(to) && to.length ? (
              <Tooltip title={(
                <div>
                  {to.map((x, i) => (
                    <div key={`${x.userId || x.email || i}`}>{x.label || x.email || x.userId}</div>
                  ))}
                </div>
              )}>
                <div style={{ color: "#667085", fontSize: 12 }}>
                  to {to.length === 1
                    ? (to[0].label || to[0].email || "1 person")
                    : `${to.length} recipients`}
                </div>
              </Tooltip>
            ) : null}
            {r.deliveryCount > 1 ? <Tag style={{ marginTop: 2 }}>{r.deliveryCount} sends</Tag> : null}
          </div>
        );
      },
    },
    {
      title: "Responsible",
      dataIndex: "responsible",
      width: 170,
      render: (list) => ((list || []).map((p) => p.name || p.email).join(", ")
        || <span style={{ color: "#98a2b3" }}>nobody yet</span>),
    },
    {
      title: "Due",
      dataIndex: "dueDate",
      width: 150,
      render: (v, r) => {
        if (r.scheduleMissing) return <Tag color="red">schedule row gone</Tag>;
        if (!v) return <span style={{ color: "#98a2b3" }}>no date</span>;
        return (
          <span>
            {v}
            {/* RED IS FOR WHAT CAN BE CHASED TODAY. `overdue` means chaseable
                now; `daysLate` is how late it was, which survives release.
                Owner, 2026-10-09: "just make the number of late days smaller
                and not red once release" - the truth kept and de-emphasised,
                not suppressed. A released row shows the figure in small grey
                type, because the sheet WAS late and he wants to see it; the
                alarm is reserved for a drawing somebody can still act on. */}
            {/* A CHASEABLE DRAWING MAY HAVE NO DAY COUNT. Its due date has
                passed while the activity's projected finish - what lateness is
                measured against - has not, so there is nothing to count yet.
                Printing the number blindly rendered "nulld late". */}
            {r.overdue ? (
              <Tag color="red" style={{ marginLeft: 6 }}>
                {r.daysLate ? `${r.daysLate}d late` : "past due"}
              </Tag>
            ) : (r.daysLate ? (
              <span style={{ marginLeft: 6, color: "#98a2b3", fontSize: 11 }}>
                {r.daysLate}d late at release
              </span>
            ) : null)}
            {/* AND WHAT THE NUMBER IS AGAINST, which is NOT the date above it.
                Owner, 2026-10-09: "date + tiny late info against proejcted" -
                the due date is what we chase, the projected finish is what
                lateness is measured against, and printing the count without
                its reference is how a number gets argued about in a meeting. */}
            {r.daysLate && r.lateAgainst && r.lateAgainst !== v ? (
              <div style={{ color: "#98a2b3", fontSize: 11 }}>
                against {r.lateAgainstSource || "the finish"} {r.lateAgainst}
              </div>
            ) : null}
            {/* WHERE THE DATE CAME FROM (owner, 2026-09-25). A drawing
                due on a date nobody can explain is a drawing whose date
                gets argued about - "from the look-ahead deadline" and
                "from the schedule finish" are different promises. */}
            {r.dueSource ? (
              <div style={{ color: "#98a2b3", fontSize: 11 }}>{r.dueSource}</div>
            ) : null}
            {/* BORN OVERDUE: shown, and explicitly NOT chased, so nobody
                waits for a reminder that is never coming. */}
            {r.bornOverdue ? (
              <div style={{ color: "#b54708", fontSize: 11 }}>
                overdue when it was created - not chased until it is re-dated
              </div>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Internal release",
      dataIndex: "internalRelease",
      width: 150,
      render: (rel, r) => (canEdit ? (
        <Button size="small" type={rel ? "primary" : "default"}
                onClick={async () => {
                  try { await setInternalRelease(projectId, r._id, !rel); load(); } catch (e) { message.error(e.message); }
                }}>
          {rel ? `Released ${moment(rel.at).format("D MMM")}` : "Mark released"}
        </Button>
      ) : (rel ? <Tag color="blue">{moment(rel.at).format("D MMM")}</Tag> : "-"))
      ,
    },
    {
      title: "Sign-off",
      dataIndex: "signedOff",
      width: 160,
      // THE GANTT'S STAR, on the drawing's activity (owner, 2026-10-10):
      // green signed off, red past its sign-off date, gold pending, and a
      // grey one with the day a moved sign-off was first planned.
      render: (v, r) => {
        const star = signOffStar(r, moment().format("YYYY-MM-DD"));
        const was = star && star.was ? (
          <div style={{ color: "#98a2b3", fontSize: 11 }} data-testid="register-signoff-original">
            <span style={{ color: STAR_COLOR.original }}>★</span> first planned {moment(star.was, "YYYY-MM-DD").format("D MMM")}
          </div>
        ) : null;
        if (v) {
          return (
            <span>
              <Tag color="green"><span data-testid="register-signoff-star" style={{ color: star.color }}>★</span> {moment(r.signedOffAt).format("D MMM")}{r.signedOffBy ? ` - ${r.signedOffBy}` : ""}</Tag>
              {was}
            </span>
          );
        }
        if (!star) return <span style={{ color: "#98a2b3" }}>not signed off</span>;
        return (
          <span title={star.kind === "overdue" ? "Sign-off date passed, not signed off" : "Sign-off due, not signed off yet"}>
            <span data-testid="register-signoff-star" style={{ color: star.color }}>☆</span>{" "}
            <span style={{ color: star.kind === "overdue" ? STAR_COLOR.overdue : undefined }}>due {moment(star.date, "YYYY-MM-DD").format("D MMM")}</span>
            <div style={{ color: "#98a2b3", fontSize: 11 }}>not signed off</div>
            {was}
          </span>
        );
      },
    },
    {
      title: "Signed off by owner/GC",
      dataIndex: "ownerSignOff",
      width: 170,
      // THE CLIENT'S SIGN-OFF, marked by the GC (owner, 2026-10-21): the blue
      // star of the drawing's level / area / system / activity, read by the
      // server (utils/ownerSignOff) - switched on Levels, Areas & Systems.
      render: (o) => (o ? (
        <span title={`${o.planned ? "Owner/GC sign-off planned for" : "Signed off by owner/GC on"} ${o.date} - ${o.scope}${o.by ? `, marked by ${o.by}` : ""}`} data-testid="register-owner-signoff">
          <span style={{ color: o.planned ? "#93c5fd" : "#1d4ed8" }}>★</span>{" "}
          <span style={{ color: "#1d4ed8", fontWeight: 600 }}>{o.planned ? "planned " : ""}{moment(o.date, "YYYY-MM-DD").format("D MMM")}</span>
          <div style={{ color: "#64748b", fontSize: 11 }}>{o.scope}</div>
        </span>
      ) : <span style={{ color: "#98a2b3" }}>-</span>),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 170,
      render: (v, r) => (
        <Space size={4} wrap>
          <Tag color={STATUS_COLOR[v] || "default"}>{STATUS_LABEL[v] || v}</Tag>
          {r.revision > 0 ? <Tag>rev {r.revision}</Tag> : null}
        </Space>
      ),
    },
    {
      // GAP 1: "The shop drawing row then shows a count of open change requests
      // next to Status." Zero is shown as a dash rather than 0 so a row with
      // none does not compete for attention with a row that has three.
      title: "Changes",
      dataIndex: "openChangeRequests",
      width: 84,
      render: (v) => (v ? <Tag color="orange">{v} open</Tag> : <span style={{ color: "#bfbfbf" }}>-</span>),
    },
    {
      // GAP 3: who gave direction, what it was, when.
      title: "Direction",
      dataIndex: "directionOutcome",
      width: 150,
      render: (v, r) => (v
        ? (
          <Tooltip title={`${v}${r.directedBy ? ` — ${r.directedBy}` : ""}`}>
            <span>{(r.directedAt || "").slice(0, 10)} <span style={{ color: "#667085" }}>{r.directedBy}</span></span>
          </Tooltip>
        )
        : <span style={{ color: "#bfbfbf" }}>-</span>),
    },
    {
      // GAP 5: Delivered says the mail left. THIS says somebody in the field
      // said they had it. Two different facts, two columns.
      title: "Received by field",
      dataIndex: "receivedByFieldAt",
      width: 150,
      render: (v, r) => (v
        ? (
          <Tooltip title={`${r.receivedByFieldBy || "someone in the field"}${r.receivedByFieldNote ? ` — ${r.receivedByFieldNote}` : ""}`}>
            <Tag color="green">{String(v).slice(0, 10)}</Tag>
          </Tooltip>
        )
        : (r.deliveredAt && !r.partnerEditOff
          ? <Button size="small" onClick={() => acknowledge(r)}>Acknowledge</Button>
          : <span style={{ color: "#bfbfbf" }}>-</span>)),
    },
    {
      // ESTIMATED HOURS (owner, 2026-10-16) beside the hours actually spent.
      title: "Est. hours",
      dataIndex: "estimatedHours",
      width: 80,
      // With the projected cost beneath, for those the server priced it for.
      render: (v, r) => (v == null ? <span style={{ color: "#bbb" }}>-</span> : (
        <span>
          {v}
          {r.projection && r.projection.cost != null ? (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{`$${Number(r.projection.cost).toLocaleString()}`}</div>
          ) : null}
        </span>
      )),
    },
    {
      title: "Hours",
      dataIndex: "hours",
      width: 80,
      // A PAIRED drawing shows the pair's one figure, wherever it was entered,
      // and says so - hours entered on the issue count for the drawing too.
      render: (v, r) => {
        const p = r.pairedIssue;
        if (p && p.hoursOn === "issue") {
          return (
            <Tooltip title={`Entered on issue ${p.label} - the pair counts one set of hours`}>
              <span>{p.hours}<div style={{ color: "#667085", fontSize: 11 }}>on issue</div></span>
            </Tooltip>
          );
        }
        return v === null || v === undefined ? "-" : v;
      },
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_, r) => (
        <div>
        <Space size={4} wrap>
          {r.docControlId ? (
            <>
              {/* THE FILE ITSELF, which is what this button looked like it
                  did and never did (owner, 2026-10-05: "link should open
                  the exact shop, and not open the doc control page"). It
                  pushed /document-control?doc=<id>, and Document Control
                  reads `?sheet=` - so the one wrong parameter turned the
                  button on a row into a trip to a list of every document
                  on the project.
                  The current revision, resolved at click time: the
                  download urls are signed and short-lived (ten minutes),
                  so one fetched with the table and clicked later would
                  401. */}
              <Tooltip title="Open this drawing - the latest revision filed">
                <Button size="small" icon={<FileTextOutlined />} loading={String(opening) === String(r._id)}
                        onClick={() => openDrawingFile(r)} />
              </Tooltip>
              {/* The filing, still one click away for anyone who wants the
                  record rather than the drawing - and deep-linked to THIS
                  document now, not to the page. */}
              <Tooltip title="Open its record in Document Control - every revision is filed there">
                <Button size="small" icon={<FolderOpenOutlined />}
                        onClick={() => history.push(`/document-control?sheet=${r.docControlId}`)} />
              </Tooltip>
            </>
          ) : null}
          {/* DELIVER IS BACK WHILE A RE-ISSUE IS OPEN. Without this clause a
              signed-off drawing that has been sent back shows no way to
              deliver the new revision - the ask would mail somebody and
              hand them a row they cannot act on. */}
          {/* NOT ON A ROW THAT WAS ONLY SENT TO THIS PARTNER: they can see
              it, but only a responsible delivers, and the server refuses
              anyone else. `youAreResponsible` is only set for partners. */}
          {canDeliver(r) ? (
            <Upload beforeUpload={(file) => { setDeliver({ row: r, file }); return false; }}
                    showUploadList={false} maxCount={1}>
              <Button size="small" icon={<UploadOutlined />} type={r.reissueOpen ? "primary" : "default"}>
                {r.reissueOpen ? "Re-deliver" : "Deliver"}
              </Button>
            </Upload>
          ) : null}
          {/* ASK FOR IT AGAIN. Only where there is something to re-issue:
              on a row with nothing delivered the server refuses and names
              the reminder instead, and a button that exists only to be
              refused is a button that should not be there. */}
          {/* UPLOAD THIS ONE TO PROCORE / ACC (owner, 2026-10-23). Only where
              there is a file to upload; the popup says which platform is not
              ready and why (SendExternalButton). */}
          {canEdit && r.docControlId ? (
            <SendExternalButton row projectId={projectId} ids={[String(r._id)]} />
          ) : null}
          {canEdit && r.docControlId && !r.reissueOpen ? (
            <Tooltip title="Ask the responsible to issue this drawing again - they re-upload with the hours for that delivery, and it is filed as the next revision">
              <Button size="small" icon={<RedoOutlined />}
                      onClick={() => setReissue({ rows: [r], reason: "", dueAt: null })} />
            </Tooltip>
          ) : null}
          {canEdit && r.reissueOpen ? (
            <Tooltip title={`Re-issue asked for${r.reissueBy ? ` by ${r.reissueBy}` : ""}${r.reissueAt ? ` on ${String(r.reissueAt).slice(0, 10)}` : ""}${r.reissueReason ? `: ${r.reissueReason}` : ""}`}>
              <Tag color="orange" style={{ marginInlineEnd: 0 }}>Re-issue asked</Tag>
            </Tooltip>
          ) : null}
          {/* SEND, and RESEND once it has been delivered. A held send says
              in the confirm that pressing it releases the hold. */}
          {canEdit && r.status === "approved" && r.docControlId ? (
            <Popconfirm
              title={r.deliveryHeld
                ? "This send was held by request. Send it now?"
                : (r.deliveredAt ? "Send the approved drawing to its recipients again?" : "Send the approved drawing to its recipients now?")}
              onConfirm={() => sendNow(r)}>
              <Button size="small" icon={<MailOutlined />} loading={sending === String(r._id)}>
                {r.deliveredAt ? "Resend" : "Send"}
              </Button>
            </Popconfirm>
          ) : null}
          {canEdit && r.pairedIssue ? (
            <Popconfirm title={`Unlink from issue ${r.pairedIssue.label}? Hours already entered stay where they are.`}
                        onConfirm={() => unlinkRow(r)}>
              <Tooltip title={`Linked to issue ${r.pairedIssue.label} - one set of hours for both`}>
                <Button size="small" icon={<DisconnectOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : null}
          {canEdit && !r.pairedIssue && r.status !== "approved" && !r.deliveredAt ? (
            <Tooltip title="Link to an issue - the two then count one set of hours">
              <Button size="small" icon={<LinkOutlined />} onClick={() => openLink(r)} />
            </Tooltip>
          ) : null}
          {canEdit ? (
            <Tooltip title="Correct this row - name, area, package, system, or the schedule activity it signs off">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
            </Tooltip>
          ) : null}
          <RowDeleteButton show={canDelete} row={r} onDelete={deleteOne} />
          {canEdit ? (
            <Tooltip title="Who receives the approved deliverable - a different list from the responsible partners">
              <Button size="small" icon={<SendOutlined />} onClick={() => openRecipients(r)}>
                {(r.recipients || []).length || ""}
              </Button>
            </Tooltip>
          ) : null}
          {canEdit && r.status === "under_review" ? (
            <>
              <Popconfirm title="Validate this drawing? The area is not signed off by this."
                          onConfirm={() => decide(r, true, "")}>
                <Tooltip title="Validate the drawing. The schedule activity is left alone.">
                  <Button size="small" type="primary" icon={<CheckOutlined />} />
                </Tooltip>
              </Popconfirm>
              {/* NEXT TO VALIDATE, where he asked for it. Separate button
                  rather than a checkbox inside the confirm, because the two
                  acts have different consequences and one of them is hard to
                  undo. */}
              <Tooltip title="This is the last sheet needed - validate it and sign off the area">
                <Button size="small" icon={<FlagOutlined />} onClick={() => askAndSignOffArea(r)} />
              </Tooltip>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => askAndReturn(r)} />
            </>
          ) : null}
        </Space>
        <SignedOffNote r={r} />
        </div>
      ),
    },
  ], moment().format("YYYY-MM-DD"), sortPref), [sortPref, canEdit, projectId, load, decide, askAndReturn, askAndSignOffArea, history, openRecipients, openEdit, acknowledge, sendNow, sending, openLink, unlinkRow, canDelete, deleteOne, nameW, setNameWidth]);

  // WHAT THE FORM PICKS FROM. The drawer shipped with four free-text
  // boxes, one of which asked a person to type a raw ganttItem _id
  // (owner, 2026-09-25: "check why add shop drawings is not loading the
  // project schedule activities and areas and existing packages and the
  // full multiselect partner"). Loaded once when the drawer opens, not
  // on every keystroke.
  const [options, setOptions] = useState(null);
  const [roster, setRoster] = useState([]);
  const [optionsBusy, setOptionsBusy] = useState(false);
  // WHICH PROJECT THE OPTIONS ARE FOR, in a ref rather than in the
  // dependency array. `options` used to be a dependency AND be set
  // inside the effect, so the moment the answer arrived the effect
  // re-ran, its cleanup set alive=false, and the .finally that clears
  // the spinner was skipped. The data was right and the field said
  // "Loading the schedule..." for ever - found by driving it on staging,
  // not by any test: every test asserted the options, none asserted that
  // the page had stopped claiming to be busy.
  const loadedFor = useRef("");
  // OWNER ON PROD, 2026-09-28: "the schedule activity on the add shop
  // drawing, is not showing any option". Nothing was wrong with the data -
  // that project has 62 usable activities - and TWO LINES HERE TURNED A
  // SLOW OR FAILED REQUEST INTO A FORM THAT SAID THE PROJECT HAD NONE:
  //
  //   .catch(() => null)  then  setOptions(o || { activities: [], ... })
  //     - every failure became an empty picker with nothing said, and the
  //       "no shop-drawing steps on this project" message below then
  //       explained the wrong thing with total confidence.
  //   loadedFor.current = projectId  BEFORE the request resolved
  //     - so once a load had failed it never retried. Closing the drawer
  //       and reopening it did nothing, which is why it looked permanent
  //       rather than flaky, and why he reported it as the form being
  //       broken rather than slow.
  //
  // Now: the latch is set ON SUCCESS ONLY, a failure is SAID and offers a
  // retry, and the two states cannot be mistaken for each other. The
  // backend half of this is in utils/shopDrawingOptions - the call was
  // taking 21.8 seconds reading a PDF for a field this form never shows.
  const [optionsError, setOptionsError] = useState("");
  const [optionsReload, setOptionsReload] = useState(0);
  // ONE LOADER, TWO TRIGGERS. Owner, 2026-09-30: "the recipient issue, is
  // that the dropdown is not allowing the multiselect from the roster like
  // the responsible dropdown".
  //
  // This effect is the ONLY thing that fetches the roster, and it used to
  // bail unless `adding` was true - so it ran for the ADD drawer and never
  // for the RECIPIENTS EDITOR, which is opened from a row and sets `recips`
  // instead. Both drawers render the same picker off the same
  // recipientOptions; one had data and one had an empty array. An empty
  // list renders perfectly, which is why it read as "multiselect does not
  // work" rather than as a failure.
  //
  // NOT a second effect: two fetchers of one thing drift. The loadedFor
  // latch still means one request per project however many times either
  // drawer is opened.
  // WHO NEEDS THE PROJECT'S OPTIONS LOADED. Owner, 2026-10-01: "schedule
  // activity signoff is not showing the list of activities in schedule on the
  // dropdown on edit sheet". The EDIT drawer reads options.activities for its
  // own activity picker and was not in this condition, so `options` stayed
  // null and the dropdown was empty - the field rendered, searched and
  // offered nothing.
  //
  // THIS IS THE THIRD CONSUMER AND THE THIRD TIME THIS LINE WAS WRONG: it
  // started as `adding`, gained `recips` when the recipients dialog needed the
  // roster, and now `edit`. A guard that ENUMERATES its consumers is a list
  // somebody has to remember to extend, and nothing fails loudly when they do
  // not - the picker just comes up empty, which reads as "this project has no
  // activities" rather than as a bug. Named for what it means now.
  const wantsProjectOptions = anyOpen(adding, recips, edit);
  useEffect(() => {
    if (!wantsProjectOptions || !projectId || loadedFor.current === projectId) return undefined;
    let alive = true;
    setOptionsBusy(true);
    setOptionsError("");
    Promise.all([
      getShopDrawingOptions(projectId).then((o) => ({ ok: o }), (e) => ({ err: e })),
      getParticipantsRoster(projectId).catch(() => null),
    ]).then(([o, r]) => {
      if (!alive) return;
      if (o.ok) {
        // Only a load that actually arrived counts as "done for this
        // project". Latching before the answer is what made one failure
        // permanent for the whole session.
        loadedFor.current = projectId;
        setOptions(o.ok);
        setOptionsError("");
      } else {
        setOptions({ activities: [], packages: [], areas: [], levels: [] });
        setOptionsError((o.err && o.err.message) || "Could not load the schedule activities");
      }
      setRoster((r && (r.roster || r.companies || r)) || []);
    }).finally(() => { if (alive) setOptionsBusy(false); });
    return () => { alive = false; };
  }, [wantsProjectOptions, projectId, optionsReload]);
  // No need to clear the latch here: it is only ever set on success, so
  // after a failure there is nothing to clear. Clearing it anyway was
  // belt-and-braces that NO TEST COULD TELL APART from not clearing it -
  // removing the line and watching nothing go red is what proved it dead.
  const retryOptions = () => setOptionsReload((n) => n + 1);

  // PICKING THE ACTIVITY FILLS THE AREA AND THE PACKAGE, because the
  // schedule row already knows both. Asking the same question twice is
  // how the two come to disagree - and a typed value that contradicts
  // its own activity is a drawing filed under a place it is not in.
  // WHICH FORM, EXPLICITLY. There are two instances on this page - `form`
  // for Add, `editForm` for Edit - and this used to close over `form`, so
  // wiring it to the Edit dropdown as it stood would have filled the Add
  // form behind a closed modal. Taking the target is what makes it safe
  // to reuse.
  const fillFromActivity = (id, target) => {
    const a = ((options && options.activities) || []).find((x) => x.id === id);
    if (!a) return;
    const patch = {};
    if (a.area) patch.area = a.area;
    if (a.package) patch.package = a.package;
    if (Object.keys(patch).length) target.setFieldsValue(patch);
  };
  const onActivity = (id) => fillFromActivity(id, form);
  // OWNER, 2026-10-02: "why the activity on edit shop drawing, per row is
  // not pulling data from schedule". Because this dropdown had NO
  // onChange: Add filled the area and the package from the chosen
  // activity, Edit silently did not, so re-pointing a row at another
  // activity left it filed under the old one's area and package.
  //
  // It matters more now than it did: he is rebuilding BA03's schedule and
  // will "add new activities in the future and reassign shopdrawings,
  // tracking, issues to them" - which is this dropdown, used in anger,
  // row after row.
  //
  // SINGLE ROW ONLY, AND THAT IS NOT LAZINESS. In a bulk edit a blank
  // field means "keep each one" - saveEdit drops `undefined` - so filling
  // area and package there would turn "untouched" into "overwrite on
  // every selected row", from one click on a different field. Anyone who
  // wants that can still type it.
  const onEditActivity = (id) => {
    if (!edit || edit.many) return;
    fillFromActivity(id, editForm);
  };

  // DECLARED HERE, NOT BESIDE editForm: `options`, `optionsBusy` and
  // `optionsError` are useState calls about 400 lines further down, so
  // reading them earlier is a temporal dead zone - a const read above
  // its declaration, which is the white-page crash shape I have logged
  // twice. It cost 41 tests here before it cost a page.
  // WHAT THE SERVER OFFERED, and the list actually rendered - two
  // different things once the orphan sentinel can be in the second.
  const serverActivities = (options && options.activities) || [];
  const editActivityOptions = activityOptions(
    serverActivities,
    editActivityValue,
    (d) => moment(d).format("D MMM YYYY"),
  );
  // ONE NODE, used by notFoundContent AND dropdownRender, because antd
  // picks between them depending on whether the list is empty - and the
  // owner saw antd's own "No data" when I had only one of the two.
  const emptyActivityNote = optionsBusy
    ? <div style={{ padding: "6px 10px", color: "#6B7280", fontSize: 12.5 }}>Loading the schedule…</div>
    : optionsError
      ? <div style={{ padding: "6px 10px", color: "#B45309", fontSize: 12.5 }}>{optionsError}</div>
      : <div style={{ padding: "6px 10px", color: "#6B7280", fontSize: 12.5, maxWidth: 360, whiteSpace: "normal", lineHeight: 1.45 }}>
          {emptyActivityMessage(options && options.emptyBecause)}
        </div>;

  // Everyone the roster knows, flat, so a chosen email carries the
  // userId and company with it - an in-app notification needs the user,
  // not an address (back/utils/shopDrawingNotify).
  const rosterPeople = useMemo(() => {
    const out = [];
    (roster || []).forEach((c) => {
      peopleOf(c).forEach((u) => {
        const email = String(u.email || "").toLowerCase();
        if (!email) return;
        out.push({
          email,
          userId: String(u.userId || ""),
          name: u.name || email,
          companyId: String(c.companyId || ""),
          companyName: c.companyName || "",
        });
      });
    });
    return out;
  }, [roster]);

  // A TAG IS AN ADDRESS; A PICKED PERSON CARRIES THEIR IDENTITY. Same rule
  // as the responsible picker above: a roster pick keeps its userId, name
  // and company, and a typed address is allowed and mailed with none of
  // them. The server refuses a malformed one and TELLS US WHICH, so the
  // dialog can say so rather than quietly keeping the good ones.
  // What the current selection actually amounts to, counted rather than
  // implied - a person reading "3 selected" cannot tell that two of them
  // have never logged in.
  // WHO IS ALREADY ON THIS PROJECT AND WHO IS NOT, SAID WHERE SOMEBODY IS
  // DECIDING. Owner, 2026-09-28: "make sure there is a UI that shows who is
  // added to proejcts and who is not". The moment that matters is the pick,
  // not a page somebody has to remember to open.
  //
  // THE TEST IS userId, NOT THE `invited` FLAG. back/routes/participants.js
  // records why: `invited` marks a pending invite for THIS project, and a
  // cold-copied roster row carries it verbatim, so on a copied row it
  // asserts an email that was never sent - which would mark people as
  // reachable on exactly the rows that need attention, and silently.
  // userId is an account-level fact that travels honestly.
  //
  // THREE STATES, AND ONLY THREE - anything else would be invented here.
  // Whether a TYPED address belongs to an account on some other project is
  // not knowable from this page, so it is not claimed.
  // The shared picker's options (components/PeoplePicker, owner
  // 2026-10-15): each partner under its trade, people tagged by kind, and
  // this page's own marker on every row. A recipient is a reader, so a
  // partner executive is offered here.
  const recipientOptions = useMemo(
    () => peoplePickerOptions(roster, { personValue: emailValue, note: shopDrawingNote }),
    [roster]
  );
  // Responsible: utils/shopDrawingResponsible refuses an executive-only
  // contact, so he is listed greyed out with the reason instead.
  const responsibleOptions = useMemo(
    () => peoplePickerOptions(roster, { personValue: emailValue, executives: "disable" }),
    [roster]
  );

  const recipientSummary = useCallback((tags) => {
    const picked = (tags || []).map((v) => String(v || "").trim().toLowerCase()).filter(Boolean);
    let onProject = 0; let noAccount = 0; let outside = 0;
    picked.forEach((email) => {
      const known = rosterPeople.find((p) => p.email === email);
      if (!known) outside += 1;
      else if (known.userId) onProject += 1;
      else noAccount += 1;
    });
    const bits = [];
    if (onProject) bits.push(`${onProject} on this project`);
    if (noAccount) bits.push(`${noAccount} on the roster with no account yet`);
    if (outside) bits.push(`${outside} not on this project`);
    return bits.join(" \u00b7 ");
  }, [rosterPeople]);

  const entriesFrom = useCallback((tags) => (tags || []).map((v) => {
    const email = String(v || "").trim().toLowerCase();
    const known = rosterPeople.find((p) => p.email === email);
    return known ? { email, userId: known.userId, name: known.name, companyId: known.companyId } : { email };
  }), [rosterPeople]);

  const saveRecipients = async () => {
    if (!recips) return;
    // MANY DRAWINGS, ONE LIST (owner, 2026-10-05: "add bulk value to be
    // assigned to all shop drawings, especially the recepient list, so we
    // don't have to input it everytime").
    //
    // AN EMPTY LIST IN BULK IS DESTRUCTIVE AND IS CONFIRMED. Everywhere else
    // on this page a blank bulk field means LEAVE ALONE; here the route
    // replaces, so blank means "remove every recipient from all of them".
    // Those two conventions must not be allowed to look alike.
    if (recips.rows && !(recips.value || []).length) {
      const ok = window.confirm(
        `Remove every recipient from ${recips.rows.length} shop drawing(s)? `
        + "Nobody outside will be mailed when they are approved."
      );
      if (!ok) return;
    }
    setRecipsBusy(true);
    try {
      // THE PROJECT DEFAULT, through its own route. Saved here rather than
      // in a settings page because it is the same list, edited the same
      // way, and a second editor for one list is a second set of rules
      // about what an address may be.
      if (recips.projectDefault) {
        const r = await setShopDrawingRecipientDefaults(projectId, entriesFrom(recips.value));
        const kept = (r.recipients || []).length;
        if ((r.rejected || []).length) {
          message.warning(`${kept} saved as the default. Not an email address: ${r.rejected.map((x) => x.value).join(", ")}`);
        } else {
          message.success(kept
            ? `${kept} default recipient(s) - they will be added to NEW shop drawings`
            : "No default recipients - new drawings start with an empty list");
        }
        setRecips(null);
        return;
      }
      if (recips.rows) {
        const r = await bulkSetShopDrawingRecipients(
          projectId, recips.rows.map((x) => String(x._id)), entriesFrom(recips.value)
        );
        // THE NUMBERS THE SERVER RETURNED, not "done" - the same shape the
        // bulk edit reports, for the same reason: modified on its own reads
        // as a partial failure when some rows were already right.
        const bits = [`${r.modified} of ${r.found} set`];
        if (r.alreadyHadValues) bits.push(`${r.alreadyHadValues} already named exactly those people`);
        if (r.missing) bits.push(`${r.missing} no longer exist`);
        message.success(bits.join(" · "));
        // REFUSALS, NAMED AND LOCATED. "Applied to 30" with four addresses
        // dropped is the shape that keeps turning up.
        if ((r.rejected || []).length) {
          const on = (r.rejectedOn || []).length;
          message.warning(
            `Not used: ${r.rejected.map((x) => `${x.value || "(blank)"} - ${x.why}`).join("; ")}`
            + (on ? ` · on ${on} drawing${on === 1 ? "" : "s"}` : ""),
            10
          );
        }
        setRecips(null);
        setPicked([]);
        load();
        return;
      }
      const r = await setShopDrawingRecipients(projectId, recips.row._id, entriesFrom(recips.value));
      const kept = (r.recipients || []).length;
      const refused = r.rejected || [];
      // SAY WHAT WAS REFUSED. An address dropped in silence is a person
      // who never gets the deliverable and nobody knows why.
      if (refused.length) {
        message.warning(`${kept} saved. Not an email address: ${refused.map((x) => x.value).join(", ")}`);
      } else {
        message.success(kept ? `${kept} recipient(s) will get the approved deliverable` : "No recipients - nobody outside will be mailed");
      }
      setRecips(null);
      load();
    } catch (e) {
      message.error(e.message || "Could not set the recipients");
    } finally {
      setRecipsBusy(false);
    }
  };

  const onAdd = async (values) => {
    try {
      await addShopDrawing(projectId, {
        ...values,
        // A PICKED PERSON CARRIES THEIR IDENTITY. An email alone can be
        // mailed and nothing else - no in-app notice, no partner
        // scoping, no reminder that knows who it is for. An address
        // typed for somebody not on the roster is still allowed and
        // still mailed, which is the issue rule: kept, and flagged by
        // having no userId.
        responsible: entriesFrom(values.responsible),
        recipients: entriesFrom(values.recipients),
      });
      message.success("Added - the responsible partners have been asked for it");
      setAdding(false);
      form.resetFields();
      load();
    } catch (e) {
      message.error(e.message || "Could not add the shop drawing");
    }
  };

  return (
    <ErrorBoundary>
      <div style={{ padding: 24 }}>
        <CoordinationBreadcrumbs />
        <Space style={{ marginBottom: 12 }} wrap>
          <Select filterOption={matchOption}
            style={{ minWidth: 280 }}
            showSearch
            optionFilterProp="label"
            placeholder="Select a project"
            value={projectId || undefined}
            onChange={(id) => history.push(`/shop-drawings/${id}`)}
            options={(projects || []).map((p) => ({ value: String(p._id), label: p.title || p.name || "Untitled" }))}
          />
          <TeamToolbar projectId={projectId} canAdd={canAdd} canRaise={keys.raise} canEdit={canEdit}
                       onAdd={() => setAdding(true)} onImport={() => setImporting(true)}
                       defaultsBusy={defaultsBusy} onDefaults={openDefaults} />
          {/* THE REGISTER AS PAPER. Everyone who can see the tab can
              export it, partner included - the server scopes the paper
              exactly as it scopes the table, so this cannot show more
              than the person is already looking at. */}
          {projectId ? (
            <>
            <Dropdown
              trigger={["click"]}
              disabled={exporting}
              menu={{
                items: [
                  { key: "pdf", icon: <FilePdfOutlined />, label: "PDF" },
                  { key: "xlsx", icon: <FileExcelOutlined />, label: "Excel" },
                  { key: "csv", icon: <FileTextOutlined />, label: "CSV" },
                ],
                onClick: ({ key }) => onExport(key),
              }}
            >
              <Button icon={<DownloadOutlined />} loading={exporting}>
                Export <DownOutlined />
              </Button>
            </Dropdown>
            <Button icon={<PrinterOutlined />} loading={printing} onClick={onPrint}>
              Print
            </Button>
            {/* GAP 6 - Kaleb's list. Delivered AND signed off, as a file. It
                REPORTS THE COUNT, because a register of nothing downloads
                exactly like a register of forty and the person cannot tell. */}
            {/* THE REGISTER IN THREE FORMATS (owner, 2026-10-20: "add the same
                pdf, excel, csv export to shop drawings register"). */}
            <Dropdown
              trigger={["click"]}
              disabled={!!registering}
              menu={{
                onClick: ({ key }) => onRegister(key),
                items: [
                  { key: "pdf", icon: <FilePdfOutlined />, label: "PDF" },
                  { key: "xlsx", icon: <FileExcelOutlined />, label: "Excel (.xlsx)" },
                  { key: "csv", icon: <FileTextOutlined />, label: "CSV" },
                ],
              }}
            >
              <Tooltip title="Only drawings that are both delivered and signed off. For every row on screen use Export.">
                <Button icon={<FileTextOutlined />} loading={!!registering}>
                  Delivered register: PDF / Excel / CSV <DownOutlined style={{ fontSize: 10 }} />
                </Button>
              </Tooltip>
            </Dropdown>
            </>
          ) : null}
        </Space>

        {/* DELIVER TO ACC / PROCORE (owner, 2026-10-18): approved revisions
            are also uploaded to the folder chosen in the project card's
            settings, beside the delivery mail - components/Integrations. */}
        {projectId ? <ShopDrawingExternalDelivery projectId={projectId} canEdit={canEdit} /> : null}

        {/* THE FILTERS, modelled on the issues list and answered by the
            SERVER - see the load() above. Every control writes into one
            object; nothing filters the rows in the browser, so the count
            under the toolbar is the count of what MATCHED and not of what
            this page happens to be holding. */}
        <ShopDrawingFilterBar
          projectId={projectId} sf={sf} facets={facets} showDeliveredTo={canEdit}
          countText={(
            <>
              {busy ? "…" : `${total.toLocaleString()} ${total === 1 ? "drawing" : "drawings"}`}
              {anyFilter ? " match" : ""}
              {capped ? ` · showing the first ${(rows || []).length.toLocaleString()}` : ""}
              {!busy && filters.search && searchLoose ? " · none has every word, closest first" : ""}
              {!busy && filters.search && !searchLoose && total > 1 ? " · best match first" : ""}
            </>
          )}
        />

        {/* ONE DRAWING, FROM A LINK, AND IT SAYS SO. A table holding a
            single row of several hundred with nothing to explain it reads
            as a filter that has gone wrong; and the way back has to be a
            button, not knowledge of how the page works. */}
        {linkedOnly ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", marginBottom: 8,
                        border: "1px solid #91caff", borderRadius: 6, background: "#f0f8ff", fontSize: 12.5 }}>
            <span>Showing the one shop drawing this link pointed at.</span>
            <Button size="small" onClick={() => { setLinkedOnly(false); load(); }}>Show the whole register</Button>
          </div>
        ) : null}

        {!projectId ? <Empty description="Pick a project" /> : (
          <Spin spinning={busy}>
            <BulkBar projectId={projectId} rows={rows} picked={picked} canEdit={canEdit} canDelete={canDelete}
                     onEdit={openEdit}
                     onRecipients={(sel) => setRecips({ rows: sel, value: [] })}
                     onReissue={(sel) => setReissue({ rows: sel, reason: "", dueAt: null })}
                     onDelete={deletePicked} onClear={() => setPicked([])} />
            <Table
              rowKey="_id"
              size="small"
              dataSource={rows || []}
              columns={columns}
              onChange={onTableChange}
              components={{ header: { cell: ResizableTitle } }}
              scroll={{ x: "max-content" }}
              // TICKS ONLY FOR THE SIDE THAT CAN EDIT. A partner seeing
              // checkboxes that lead to a 403 is worse than no checkboxes.
              // PRIOR REVISIONS, on demand. Only rows that HAVE a filed document can
              // be opened - an expander on a row with nothing behind it is a
              // promise the panel cannot keep.
              // THE ROW THE LINK NAMED, MARKED. Inline rather than a class:
              // this page carries no stylesheet of its own, and a class
              // nothing defines is a highlight that silently does nothing.
              onRow={(r) => (markId && String(r._id) === String(markId)
                ? { style: { background: "#f0f8ff" } } : {})}
              expandable={{
                rowExpandable: (r) => !!r.docControlId,
                expandedRowKeys: openRows,
                onExpandedRowsChange: (keys) => setOpenRows(keys.map(String)),
                onExpand: (open, r) => { if (open) openRevs(r.docControlId); },
                expandedRowRender: (r) => {
                  const st = revs[String(r.docControlId)] || {};
                  if (st.loading) return <span style={{ color: "#667085" }}>Reading the revisions…</span>;
                  if (st.err) return <span style={{ color: "#cf1322" }}>{st.err}</span>;
                  const list = st.list || [];
                  if (!list.length) return <span style={{ color: "#667085" }}>No revisions filed yet.</span>;
                  // Highest rev is current; every earlier one is superseded, and
                  // superseded WHEN THE NEXT ONE LANDED - the same rule the
                  // server applies in utils/shopDrawings, said once here for the
                  // list the person is actually reading.
                  const top = list.reduce((hi, v) => (Number(v.rev) > hi ? Number(v.rev) : hi), -1);
                  const byRev = [...list].sort((a, b) => Number(b.rev) - Number(a.rev));
                  const dateOf = (v) => (v.createdAt || v.deliveredAt || "").slice(0, 10);
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {byRev.map((v) => {
                        const current = Number(v.rev) === top;
                        const next = byRev.find((x) => Number(x.rev) === Number(v.rev) + 1);
                        return (
                          <div key={v.rev} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                            <b style={{ width: 54 }}>Rev {v.rev}</b>
                            {current
                              ? <Tag color="green">Current</Tag>
                              : <Tag>Superseded{next && dateOf(next) ? ` ${dateOf(next)}` : ""}</Tag>}
                            <span style={{ color: "#667085" }}>
                              {dateOf(v) ? `delivered ${dateOf(v)}` : "date not recorded"}
                              {v.createdByName ? ` · ${v.createdByName}` : ""}
                            </span>
                            {/* THE DOWNLOAD HE ASKED TO KEEP. The url is the
                                server's own signed one; nothing here constructs
                                a storage path. */}
                            {v.url ? (
                              <a href={`${url}${v.url}`} target="_blank" rel="noreferrer" style={{ marginLeft: "auto" }}>
                                Download rev {v.rev}
                              </a>
                            ) : <span style={{ marginLeft: "auto", color: "#667085" }}>no file on this server</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                },
              }}
              rowSelection={selectionFor({ canEdit, canDelete, picked, setPicked })}
              pagination={{ pageSize: 25, hideOnSinglePage: true }}
              locale={{ emptyText: <Empty description="No shop drawings on this project yet" /> }}
            />
          </Spin>
        )}

        {/* THE EDITOR. Same form for one row and for many; see openEdit. */}
        <Modal
          title={edit ? edit.title : ""}
          open={!!edit}
          onCancel={() => setEdit(null)}
          onOk={() => editForm.submit()}
          okText="Save"
          confirmLoading={editBusy}
          destroyOnClose
        >
          {/* initialValues IS READ AT MOUNT, AND destroyOnClose ON THE MODAL
              ABOVE IS WHAT MAKES THAT HAPPEN ON EVERY OPEN. That prop is
              load-bearing for this: removing it fails "a SECOND open is
              populated", because the form would then keep its first mount and
              never read initialValues again.
              I also tried a per-open `key` here. It is DEAD CODE - removing it
              entirely left all thirteen tests green, because destroyOnClose
              already remounts. Belt-and-braces that no test can tell apart
              from its absence does not earn a line. */}
          <Form form={editForm} layout="vertical"
                onFinish={saveEdit} preserve={false}
                initialValues={(edit && edit.initial) || {}}>
            {edit && edit.many ? (
              <div style={{ marginBottom: 10, color: "#667085", fontSize: 12.5 }}>
                Anything left blank is not changed. Clearing a box on purpose does clear the field.
              </div>
            ) : null}
            {/* NAME IS ROW-ONLY. Sixty drawings with the same name is not a
                register, so the field is not offered for many - and saveEdit
                strips it as well, because a hidden field is a UI choice and the
                write needs its own guard. */}
            {edit && !edit.many ? (
              <Form.Item name="name" label="Name" rules={[{ required: true, message: "Give it a name" }]}>
                <Input />
              </Form.Item>
            ) : null}
            <Form.Item name="area" label="Area">
              <Select filterOption={matchOption} allowClear showSearch optionFilterProp="label" placeholder={edit && edit.many ? "Leave blank to keep each one" : ""}
                      options={shopAreaOptions(options, facets.areas)} />
            </Form.Item>
            <Form.Item name="package" label="Package">
              <Select filterOption={matchOption} allowClear showSearch placeholder={edit && edit.many ? "Leave blank to keep each one" : ""}
                      options={facets.packages.map((a) => ({ value: a, label: a }))} />
            </Form.Item>
            <Form.Item name="system" label="System">
              <Input placeholder={edit && edit.many ? "Leave blank to keep each one" : ""} />
            </Form.Item>
            <Form.Item name="estimatedHours" label="Estimated hours">
              <InputNumber min={0.25} step={0.25} style={{ width: 160 }} aria-label="Estimated hours"
                           disabled={!canEditEstimate}
                           placeholder={edit && edit.many ? "Keep each one" : ""} />
            </Form.Item>
            {/* PROJECTED COST (owner, 2026-10-16): the estimate at the rate the
                cost report prices this drawing at, following the hours as typed. */}
            {edit && edit.projection ? (
              <Form.Item noStyle shouldUpdate={(a, b) => a.estimatedHours !== b.estimatedHours}>
                {({ getFieldValue }) => (
                  <div style={{ margin: "-12px 0 12px", fontSize: 12, color: "#667085" }}>
                    {`Projected cost: ${projectedCostLine(edit.projection, getFieldValue("estimatedHours"))}`}
                  </div>
                )}
              </Form.Item>
            ) : null}
            <Form.Item name="ganttItemId" label="Schedule activity it signs off"
                       extra="The due date and sign-off come from here. An activity with no date is refused.">
              {/* THE SAME LABEL, TOOLTIP AND GREYING AS THE ADD FORM. Owner,
                  2026-10-01: "make sure all activities have the sequence from
                  parent to child using the ... for long names, and make sure
                  the tool tip shows everything". This dropdown shipped with
                  `label: a.name`, so it showed "Area A" nine times with
                  nothing to tell them apart - the exact confusion the path was
                  introduced for on the Add form, one screen away.
                  A row with no sign-off date is OFFERED AND DISABLED with its
                  reason rather than hidden: the server refuses it, and BA03
                  has 43 shop-drawing rows and ZERO sign-off dates, so hiding
                  them would leave an empty picker - which reads as broken. */}
              {/* AN ORPHANED VALUE READS AS WORDS, NOT AS ITS ID. Owner on
                  BA03: "schedule activity on shop drawing edit still show a
                  default GUID and when i click inside the dropdown, no
                  activity shows up." Both are one cause - he deleted 615
                  activities on purpose, leaving ZERO live shop-drawing rows,
                  so the list is correctly empty AND the row's value now
                  matches nothing, which makes antd render the raw id.
                  activityOptions injects one disabled sentinel for it. The
                  deleted filter STAYS: offering activities the server
                  refuses is the 62-offered-62-refused bug. */}
              <Select filterOption={matchOption} allowClear {...LEAF_SELECT_PROPS} loading={optionsBusy}
                      onChange={onEditActivity}
                      placeholder={edit && edit.many ? "Leave blank to keep each one" : ""}
                      /* BOTH, AND IT TAKES BOTH - which I got wrong once and
                         the owner found: "the dropdown of activities still
                         says no data".
                         notFoundContent replaces antd's own "No data" when
                         the options list is EMPTY, which is the case for a
                         drawing with no activity set on a project with none
                         live - his exact state. I had removed it when I
                         moved the note to dropdownRender, so antd's default
                         came back and said "No data" to him.
                         dropdownRender covers the OTHER case, where the
                         orphan sentinel makes the list non-empty so
                         notFoundContent never fires. One condition, two
                         places, because antd chooses between them. */
                      notFoundContent={emptyActivityNote}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {/* ONLY when the menu is NOT empty, or the note
                              would appear twice - notFoundContent is already
                              showing it in the empty case. The sentinel-only
                              list is exactly that: non-empty menu, nothing
                              the server offered. */}
                          {!serverActivities.length && editActivityOptions.length
                            ? emptyActivityNote
                            : null}
                        </>
                      )}
                      popupMatchSelectWidth={false}
                      dropdownStyle={{ maxWidth: 760 }}
                      options={editActivityOptions} />
            </Form.Item>
            <PickedActivity form={editForm} activities={serverActivities} />
            {edit && !edit.many ? (
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
            ) : null}
          </Form>
        </Modal>

        <Modal
          title={linking ? `Link "${linking.row.name}" to an issue` : ""}
          open={!!linking}
          onCancel={() => setLinking(null)}
          onOk={saveLink}
          okText="Link"
          okButtonProps={{ disabled: !(linking && linking.chosen), loading: !!(linking && linking.busy) }}
          destroyOnClose
        >
          {linking && linking.options === null ? <Spin /> : null}
          {linking && linking.options && linking.options.length === 0 ? (
            <div>No issue on this project can be linked: every issue is already linked to another drawing, or abandoned.</div>
          ) : null}
          {linking && linking.options && linking.options.length > 0 ? (
            <>
              <Select filterOption={matchOption}
                showSearch
                optionFilterProp="label"
                placeholder="Pick an issue"
                style={{ width: "100%" }}
                value={linking.chosen}
                onChange={(v) => setLinking((cur) => ({ ...cur, chosen: v }))}
                options={linking.options.map((i) => ({
                  value: i._id,
                  label: [
                    i.displayId != null ? `${i.artifactPrefix || ""}${i.displayId}` : "",
                    i.title,
                    i.area ? `(${i.area})` : "",
                  ].filter(Boolean).join(" "),
                }))}
              />
              <div style={{ color: "#667085", fontSize: 12, marginTop: 8 }}>
                The two then count one set of hours: whichever side has hours entered first, the other is told they are already accounted for.
              </div>
            </>
          ) : null}
        </Modal>
        <BulkSheetImportModal
          open={importing}
          projectId={projectId}
          onClose={() => setImporting(false)}
          onDone={load}
        />
        <Drawer title="Add shop drawing" open={adding} onClose={() => setAdding(false)} width={480}>
          <Form form={form} layout="vertical" onFinish={onAdd}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: "Give it a name" }]}>
              <Input placeholder="Busway shop drawing" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>
            {/* ESTIMATED HOURS (owner, 2026-10-16), required on every new
                drawing - the contractor cost page projects cost from it. */}
            <Form.Item name="estimatedHours" label="Estimated hours"
                       rules={[{ required: true, message: "Enter the estimated hours" }]}>
              <InputNumber min={0.25} step={0.25} style={{ width: 160 }} aria-label="Estimated hours" />
            </Form.Item>
            {/* PAIR IT WITH AN ISSUE - the two then count ONE set of hours,
                and whichever side records them first locks the other BY
                MENTION. Only shown when there is something to offer: an empty
                picker is a question nobody asked. */}
            {pairIssues.length > 0 && (
              <Form.Item name="pairedIssueId" label="Pair with an issue"
                         tooltip="The pair counts ONE set of hours. Enter them on either side and the other will say they are already accounted for. If the issue has a sheet attached, pairing also queues this drawing for delivery.">
                <Select filterOption={matchOption}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Not paired"
                  options={pairIssues.map((i) => ({
                    value: i._id,
                    label: [
                      i.displayId != null ? `${i.artifactPrefix || ""}${i.displayId}` : "",
                      i.title,
                      i.area ? `(${i.area})` : "",
                    ].filter(Boolean).join(" "),
                  }))}
                />
              </Form.Item>
            )}
            {/* THE SCHEDULE ACTIVITY, PICKED RATHER THAN TYPED. This asked
                for a raw ganttItem _id until 2026-09-25 - a field nobody
                outside the database could fill. Nearest due first, so the
                one being chased is under the cursor. */}
            <Form.Item name="ganttItemId" label="Schedule activity"
                       tooltip="Its due date and its sign-off come from this activity - there is no second date here"
                       rules={[{ required: true, message: "Pick the activity this drawing signs off" }]}>
              <Select filterOption={matchOption}
                {...LEAF_SELECT_PROPS}
                loading={optionsBusy}
                onChange={onActivity}
                placeholder={optionsBusy ? "Loading the schedule..." : "Search this project's shop-drawing activities"}
                notFoundContent={optionsBusy
                  ? <Spin size="small" />
                  : optionsError
                    ? (
                      // A FAILED LOAD AND AN EMPTY SCHEDULE ARE DIFFERENT
                      // THINGS and must never print the same sentence. The
                      // message below is a confident statement about the
                      // project; saying it when the request never arrived
                      // is how the owner came to believe his 62 activities
                      // did not exist.
                      <div style={{ padding: "8px 4px", color: "#B45309", fontSize: 12.5 }}>
                        Could not load the schedule activities.
                        <div style={{ color: "#6B7280", marginTop: 2 }}>
                          This is not the same as the project having none - nothing has been read.
                        </div>
                        <Button size="small" style={{ marginTop: 6 }} onClick={retryOptions}>Try again</Button>
                      </div>
                    )
                    : (
                      // AN EMPTY LIST MUST SAY WHY. antd's default here is
                      // "No data", which reads exactly like the broken form
                      // this replaced - and spaceX-test on staging has no
                      // shop-drawing steps at all, so this is the state a
                      // real project is in rather than an edge case.
                      <div style={{ padding: "8px 4px", color: "#6B7280", fontSize: 12.5 }}>
                        No shop-drawing steps on this project&apos;s schedule.
                        Add them to the plan first - the drawing hangs off the activity that signs it off.
                      </div>
                    )}
                // THE FULL PATH, the digest's string from the server, and
                // searchable by any part of it (owner, 2026-10-13). Built by
                // the same activityOptions the Edit dialog uses. The path
                // comes from the SERVER: the payload is filtered to
                // shop-drawing rows and none of their ancestors is one, so
                // there is nothing on the client to walk. Unusable rows are
                // OFFERED BUT DISABLED with the reason, never hidden.
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 760 }}
                options={activityOptions((options && options.activities) || [], null, (d) => moment(d).format("D MMM YYYY"))}
              />
            </Form.Item>
            <PickedActivity form={form} activities={(options && options.activities) || []} />
            {/* Filled from the activity when one is picked; still typeable,
                because a drawing can legitimately name a place the
                schedule has not got a row for - kept and flagged, as on
                issues. */}
            <Form.Item name="area" label="Area"
                       tooltip="Filled in from the activity. A value you type yourself is kept and marked as unlisted.">
              <Select filterOption={matchOption}
                showSearch allowClear
                mode="tags" maxTagCount={1}
                placeholder="Pick or type an area"
                optionFilterProp="label"
                options={shopAreaOptions(options)}
              />
            </Form.Item>
            <Form.Item name="package" label="Package"
                       tooltip="The work packages this project's schedule uses">
              <Select filterOption={matchOption}
                showSearch allowClear
                mode="tags" maxTagCount={1}
                placeholder="Pick or type a package"
                options={((options && options.packages) || []).map((p) => ({ value: p.value, label: p.value }))}
              />
            </Form.Item>
            {/* THE ROSTER, GROUPED BY COMPANY - the same people the issue
                manager offers. Free tags mailed an address and recorded
                nothing else: no user, so no in-app notice and no reminder
                that knows who it is for. An unlisted address is still
                allowed and still mailed. */}
            <Form.Item name="responsible" label="Responsible partners"
                       tooltip="They are emailed with a link to deliver it, and reminded as the date approaches">
              <Select
                mode="tags"
                tokenSeparators={[",", " "]}
                loading={optionsBusy}
                placeholder="Pick from the project's partners, or type an address"
                filterOption={filterPeopleOption}
                optionRender={renderPeopleOption}
                maxTagCount="responsive"
                maxTagPlaceholder={peopleTagPlaceholder(responsibleOptions)}
                options={responsibleOptions}
              />
            </Form.Item>
            <Form.Item name="recipients" label="Recipients for the approved deliverable"
                       tooltip="They are mailed ONCE, when it is approved, with the file or a download link - not on every change. Anyone can be named, on the roster or not.">
              <Select
                mode="tags"
                tokenSeparators={[",", " "]}
                loading={optionsBusy}
                placeholder="Search the project's partners, or type any address"
                filterOption={filterPeopleOption}
                optionRender={renderPeopleOption}
                maxTagCount="responsive"
                maxTagPlaceholder={peopleTagPlaceholder(recipientOptions)}
                options={recipientOptions}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit">Add</Button>
          </Form>
        </Drawer>

        <Modal
          title={recips
            ? (recips.projectDefault
              ? "Default recipients for new shop drawings"
              : (recips.rows
                ? `Recipients for ${recips.rows.length} shop drawing${recips.rows.length === 1 ? "" : "s"}`
                : `Recipients for "${recips.row.name}"`))
            : ""}
          open={!!recips}
          onCancel={() => setRecips(null)}
          onOk={saveRecipients}
          confirmLoading={recipsBusy}
          okText="Save recipients"
          destroyOnClose
        >
          <p style={{ color: "#555", marginBottom: 10 }}>
            They are mailed <strong>once, when it is approved</strong>, with the file attached or a
            download link if it is too large. They are not told about revisions or changes - that is
            the responsible partners.
          </p>
          {/* AT CREATION ONLY, SAID BEFORE IT IS SAVED. The assumption
              somebody will otherwise make is the opposite one - that this
              is the list, live, for every drawing - and they would only
              find out by changing it and watching the register not move. */}
          {recips && recips.projectDefault ? (
            <p style={{ color: "#0958d9", background: "#e6f4ff", border: "1px solid #91caff",
                        borderRadius: 4, padding: "6px 8px", marginBottom: 10, fontSize: 12.5 }}>
              This is copied onto <strong>new</strong> shop drawings as they are created. Drawings that
              already exist keep the recipients they have - changing this does not touch them.
            </p>
          ) : null}
          {recips && recips.rows ? (
            <p style={{ color: "#8c6d1f", background: "#fffbe6", border: "1px solid #ffe58f",
                        borderRadius: 4, padding: "6px 8px", marginBottom: 10, fontSize: 12.5 }}>
              This <strong>replaces</strong> the list on all {recips.rows.length} of them. Anyone
              already on a drawing keeps the record of who first named them; everyone else is
              recorded as named by you.
            </p>
          ) : null}
          <Select
            mode="tags"
            style={{ width: "100%" }}
            tokenSeparators={[",", " "]}
            value={recips ? recips.value : []}
            onChange={(v) => setRecips((cur) => (cur ? { ...cur, value: v } : cur))}
            placeholder="Search the project's partners, or type any address"
            filterOption={filterPeopleOption}
            optionRender={renderPeopleOption}
            options={recipientOptions}
          />
          {recips && recips.value && recips.value.length ? (
            <p style={{ color: "#555", marginTop: 8, fontSize: 12.5 }}>
              {recipientSummary(recips.value)}
            </p>
          ) : null}
        </Modal>

        <Modal title={deliver ? `Deliver "${deliver.row.name}"` : ""}
               open={!!deliver} onCancel={() => setDeliver(null)} footer={null} destroyOnClose>
          <p style={{ color: "#667085" }}>
            {deliver ? deliver.file.name : ""} will be filed in Document Control automatically, as a shop drawing.
          </p>
          <Form form={hoursForm} layout="vertical" onFinish={sendDelivery}>
            <Form.Item name="hoursSpent" label="Hours spent"
                       rules={[{ required: true, message: "Hours spent is required" }]}>
              <InputNumber min={0.25} step={0.25} style={{ width: 160 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={delivering} disabled={delivering}>Deliver</Button>
          </Form>
        </Modal>

        {/* ASK FOR A RE-ISSUE - one drawing or the whole tick, one dialog.
            ONE REASON FOR THE WHOLE PICK: a form that asked per drawing is
            a form nobody fills, and the case this exists for - a package
            came back with the same comment on all of it - has one reason by
            nature. It is REQUIRED, because somebody is being asked to redo
            work and bill hours for it, and "please re-issue" with no reason
            is a question they have to ask by reply. */}
        <Modal
          title={reissue
            ? (reissue.rows.length === 1
              ? `Request a re-issue of "${reissue.rows[0].name}"`
              : `Request a re-issue of ${reissue.rows.length} shop drawings`)
            : ""}
          open={!!reissue}
          onCancel={() => setReissue(null)}
          onOk={sendReissue}
          okText="Send the request"
          confirmLoading={reissueBusy}
          destroyOnClose
        >
          <p style={{ color: "#667085", marginTop: 0 }}>
            The {reissue && reissue.rows.length === 1 ? "responsible" : "responsibles"} will be told, and asked to
            upload the new revision with the hours for that delivery. It is filed in Document Control as the next
            revision of the same document, so everything already delivered stays where it is.
          </p>
          <Input.TextArea
            rows={3}
            placeholder="Why does it have to be issued again? (optional)"
            value={reissue ? reissue.reason : ""}
            onChange={(e) => setReissue((prev) => (prev ? { ...prev, reason: e.target.value } : prev))}
          />
          {/* SAID, NOT ENFORCED. The reason is what the responsible acts on,
              so it is worth asking for - but it is his call whether this
              particular ask needs one, and it often does not because he has
              already said it on a call. */}
          {reissue && !String(reissue.reason || "").trim() ? (
            <p style={{ color: "#667085", fontSize: 12, marginTop: 6, marginBottom: 0 }}>
              Without a reason the request still goes - it just says the drawing has to be issued again.
            </p>
          ) : null}
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#667085", fontSize: 12.5 }}>Asked for by (optional)</span>
            <DatePicker
              value={reissue ? reissue.dueAt : null}
              onChange={(d) => setReissue((prev) => (prev ? { ...prev, dueAt: d } : prev))}
            />
          </div>
          {/* SAID BEFORE IT IS PRESSED, not discovered in the result: the
              rows that cannot be asked about are visible here, so the
              number in the button is the number that will happen. */}
          {reissue && reissue.rows.some((r) => !r.docControlId) ? (
            <p style={{ color: "#d46b08", marginTop: 10, marginBottom: 0, fontSize: 12.5 }}>
              {reissue.rows.filter((r) => !r.docControlId).length} of these have nothing delivered yet, so there is no
              revision to issue again - they will be left alone and named afterwards. Send those a reminder instead.
            </p>
          ) : null}
          {reissue && reissue.rows.some((r) => r.reissueOpen) ? (
            <p style={{ color: "#d46b08", marginTop: 6, marginBottom: 0, fontSize: 12.5 }}>
              {reissue.rows.filter((r) => r.reissueOpen).length} of these have already been asked for and not yet
              delivered - they will be left alone rather than asked twice.
            </p>
          ) : null}
        </Modal>
      </div>
    </ErrorBoundary>
  );
}
